/**
 * Runtime - manages graphs, instances, and data flow
 *
 * Key concepts:
 * - Nodes: leaf-level nodes with actual code (Clock, PatternPlayer, etc.)
 * - Graphs: compositions of nodes or other graphs
 * - Instances: runtime instantiations of graphs with their own values
 * - InternalConnections: wiring within a graph (part of the blueprint)
 * - InstanceConnections: wiring between instances
 *
 * Config architecture for encapsulated graphs:
 *
 * 1. EXPOSED endpoints (in definition.api):
 *    - Each instance can have its own value
 *    - Stored in parent's config: parentDef.config[instanceId][exposedEndpoint]
 *    - Example: rootDef.config.mySubgraph.volume = -10
 *
 * 2. NON-EXPOSED (internal) endpoints:
 *    - Shared across ALL instances of the definition
 *    - Stored in the definition's own config: definition.config[internalNode][endpoint]
 *    - Example: subgraphDef.config.synth_rh6846h.detune = 5
 *    - Changing internal config propagates to all instances
 *
 * When an endpoint is unexposed, any stale config values in parent definitions
 * must be cleaned up to prevent "Unknown endpoint" errors during state restore.
 */

import type { IRuntime, NodeDef } from './IRuntime';
import type { ITransport } from './ITransport';
import type { INode } from '../nodes/INode';
import type { Endpoint } from '../nodes/Endpoint';
import type { IEndpoint } from './IEndpoint';
import { logger } from '../logging';
import type { RuntimeEnv } from './RuntimeEnv';
import { EndpointWithContext, PluginRegistry, type Plugin } from '@plugins/Plugin';
import { installedPlugins } from '@/plugins/installedPlugins';
import { initAudioPlugin } from '@/plugins/audio/audioPlugin';
import { type GraphDefinition, type ExposedEndpoint, getNodeType, toNodeConfig } from '../graph/GraphDefinition';
import type { IGraphInstance } from '../graph/IGraphInstance';
import type { InstanceConnection } from '../graph/Connection';
import { GraphInstance } from '../graph/GraphInstance';
import { encapsulate as encapsulateNodes } from '../graph/encapsulate';
import { fromEndpointPath, resolveConfigValue, resolveExposedPath, getExposedEndpointDefaults, toConfigObject } from './util';
import { IRuntimeStore, RuntimeStore } from './RuntimeStore';
import { loadGraphPreset, isMetadataDescriptor, type Snapshot } from '../nodes/PresetManager';
import { type EndpointsRecord, params, internals } from '@nodes/TypedEndpoints';
import { AudioEndpoint } from '@/plugins/audio/AudioEndpoint';
import { getVideoRenderer, registerVideoRenderer, unregisterVideoEntry, clearVideoRegistry } from '@/plugins/video/videoRegistry';
import { clearAudioRegistry } from '@/plugins/audio/audioRegistry';
import type { ICanvasRenderer } from '@/plugins/video/nodes/VideoCanvasNode';
import { BaseDependencies, type RepeaterDomainAdapter } from '@/nodes/Dependencies';
import { GraphIndex } from './GraphIndex';
import { type GetterFactory } from '@/plugins/data/DataEndpoint';

/**
 * Convert object-format endpoints to array format with id property.
 * Used for backward compatibility with createEndpoints and other array-expecting code.
 */
function endpointsToArray(endpoints: EndpointsRecord): Endpoint[] {
  return Object.entries(endpoints).map(([id, config]) => ({ id, ...config } as Endpoint));
}

/**
 * Extract custom params from node config.
 * A config entry becomes a custom param if it's an object with no matching base endpoint.
 * Custom params get sensible defaults via params() helper (dataType: 'number', etc).
 */
function getCustomParams(
  nodeConfig: Record<string, any> | undefined,
  baseEndpoints: EndpointsRecord
): Endpoint[] {
  if (!nodeConfig) return [];

  const dynamicConfigs: Record<string, any> = {};

  for (const [endpointId, config] of Object.entries(nodeConfig)) {
    // Skip if there's a matching base endpoint (this is just a value override)
    if (baseEndpoints[endpointId]) continue;

    // Object configs without a base endpoint become dynamic param endpoints
    if (config && typeof config === 'object' && !Array.isArray(config)) {
      dynamicConfigs[endpointId] = config;
    }
  }

  // Split by dataFlow: internal endpoints use internals(), everything else uses params()
  const paramConfigs: Record<string, any> = {};
  const internalConfigs: Record<string, any> = {};
  for (const [id, config] of Object.entries(dynamicConfigs)) {
    if (config.dataFlow === 'internal') internalConfigs[id] = config;
    else paramConfigs[id] = config;
  }

  const result: Endpoint[] = [];
  for (const [id, ep] of Object.entries(params(paramConfigs))) {
    result.push({ id, ...ep, custom: true } as Endpoint);
  }
  for (const [id, ep] of Object.entries(internals(internalConfigs))) {
    result.push({ id, ...ep, custom: true } as Endpoint);
  }
  return result;
}

/**
 * A factory that takes context and returns a getter (dt) => any.
 * The user writes `(dt) => value + bar`, which is compiled as:
 *   function({value, min, max, step, _ppq, _bpb}) {
 *     return (dt) => { var bar = ...; var beat = ...; return ((dt) => value + bar)(dt); };
 *   }
 * bar/beat are computed from dt each call so they stay fresh.
 * _ppq = pulses per quarter note, _bpb = beats per bar (time signature numerator).
 */

/**
 * Compile a getter source string to a context factory.
 * Returns null if source is empty or compilation fails.
 */
function compileGetter(source: string): GetterFactory | null {
  if (!source?.trim()) return null;
  try {
    // Legacy arrow function format "(dt) => expr" — invoke it with dt
    // New expression format "Math.sin(t * 0.01)" — wrap as return statement
    const isArrowFn = /^\s*(\([^)]*\)|[a-zA-Z_$]\w*)\s*=>/.test(source);
    const body = source.includes('\n') ? source
      : isArrowFn ? `return (${source})(dt);`
      : `return ${source};`;
    const factory: GetterFactory = eval(
      `(function(_c) {` +
      ` return function(dt) {` +
      `  dt = dt ?? _c._time; var t = dt;` +
      `  var value = _c.value; var min = _c.min; var max = _c.max; var step = _c.step;` +
      `  var host = _c.host; var rnd = _c.rnd;` +
      `  var bar = Math.floor(t / (_c._ppq * _c._bpb)) + 1;` +
      `  var beat = Math.floor((t % (_c._ppq * _c._bpb)) / _c._ppq) + 1;` +
      `  ${body}` +
      ` };` +
      `})`
    );
    factory._source = source;
    return factory;
  } catch (e) {
    logger.error('compileGetter', `Failed to compile: ${e}`);
    return null;
  }
}

/** Get sibling param endpoints for a node, excluding the given endpoint */
function getSiblingEndpoints(instance: IGraphInstance, nodeId: string, excludeEpId: string): Record<string, IEndpoint> {
  const prefix = `${nodeId}.`;
  const result: Record<string, IEndpoint> = {};
  for (const [key, ep] of Object.entries(instance.endpoints)) {
    if (key.startsWith(prefix)) {
      const epId = key.slice(prefix.length);
      if (epId !== excludeEpId) result[epId] = ep;
    }
  }
  return result;
}

export interface ValueChangedArgs {
  instanceId: string;
  nodeId: string;
  endpointId: string;
  value;
  direction: 'in' | 'out'
}

export interface NodesArgs {
  instanceId: string;
  nodeIds: string[];
}

export interface ConnectionArgs {
  instanceId: string;
  connection: { source: string; target: string; disabled?: boolean };
}

export interface DefinitionChangedArgs {
  definitionId: string;
  definition: GraphDefinition;
  newNodeIds?: string[];  // For auto-layout of newly added nodes
}



/*
export type EndpointActivityCallback = (instanceId: string, nodeId: string, endpointId: string, direction: 'in' | 'out') => void;
export type ValueChangeCallback = (instanceId: string, nodeId: string, endpointId: string, value: any) => void;
export type AudioActivityCallback = (instanceId: string, nodeId: string, endpointId: string, level: number) => void;
export type ErrorCallback = (error: { message: string; nodeId: string; endpointId: string; timestamp: number }) => void;
*/
export class Runtime implements IRuntime {
  private graphDefinitions: Record<string, GraphDefinition> = {};
  private instances: Record<string, IGraphInstance> = {};
  private instancesByDefinition: Record<string, Set<string>> = {};
  private pluginRegistry: PluginRegistry;

  /** Domain adapters for template-based Repeater, keyed by output dataType */
  private repeaterAdapters: Map<string, RepeaterDomainAdapter> = new Map();

  // Maps instanceId -> containing instance ID for O(1) lookup
  private containedBy: Record<string, string> = {};

  // Track which internal connections have been wired (for sync diffing)
  // Key: instanceId, Value: Set of "source->target" strings
  private wiredConnections: Record<string, Set<string>> = {};

  // Environment dependencies (transport, audioContext, etc.)
  protected readonly env: RuntimeEnv;

  // Track previous audio levels to only emit on significant changes
  // private lastAudioLevels: Map<string, number> = new Map();

  private store: IRuntimeStore;

  /** Queryable graph index for structural queries */
  readonly graphIndex = new GraphIndex();

  // Loading flag - suppresses emissions to store during project load
  private loading = false;

  constructor(
    store: IRuntimeStore,
    env: RuntimeEnv) {
    this.env = env;
    this.store = store;
    this.pluginRegistry = new PluginRegistry(store);

    // Provide audioContext to audio plugin before registration
    initAudioPlugin(env.audioContext);

    // Register installed plugins
    for (const plugin of installedPlugins) {
      this.registerPlugin(plugin);
    }

    // Give store access to registry for node metadata lookups
    if ('setPluginRegistry' in store) {
      (store as RuntimeStore).setPluginRegistry(this.pluginRegistry);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Data Flow Methods (per ENDPOINT_DATA_FLOW.md)
  // ─────────────────────────────────────────────────────────────

  /**
   * Apply a tweak (from UI/REPL).
   * Updates config which propagates to all instances of the definition.
   */
  applyTweak(instanceId: string, nodeId: string, endpointId: string, value: any): void {
    const instance = this.instances[instanceId];
    if (!instance) {
      throw new Error(`applyTweak: instance '${instanceId}' not found`);
    }

    // Update config and propagate to all instances (including this one)
    // setGraphConfig calls setEndpointFromConfig for each instance which handles:
    // store update, node notification, and propagation
    this.setGraphConfig(instance.definitionId, nodeId, endpointId, 'value', value);
  }

  /**
   * Recall a project-level snapshot — applies tweakable param values across definitions.
   * Silently skips keys where the definition or node no longer exists.
   */
  recallSnapshot(snapshot: Snapshot): void {
    for (const [definitionId, defValues] of Object.entries(snapshot.values)) {
      const definition = this.graphDefinitions[definitionId];
      if (!definition) continue;

      for (const [key, value] of Object.entries(defValues)) {
        const dotIndex = key.indexOf('.');
        if (dotIndex === -1) continue;
        const nodeId = key.slice(0, dotIndex);
        const endpointId = key.slice(dotIndex + 1);

        if (!definition.nodes[nodeId]) continue;

        this.setGraphConfig(definitionId, nodeId, endpointId, 'value', value);
      }
    }
  }

  /**
   * Load a preset for a node or GraphInstance.
   *
   * For primitives: looks up preset from NodeDef.factoryPresets
   * For GraphInstances: looks up from GraphDefinition.presets + userPresets
   *
   * Each preset param is applied via applyTweak (persists to config).
   */
  loadPreset(instanceId: string, nodeId: string | null, presetName: string): void {
    if (nodeId === null) {
      // GraphInstance preset - instanceId IS the GraphInstance
      this.loadGraphInstancePreset(instanceId, presetName);
    } else {
      // Check if this node is actually a GraphInstance (sub-graph)
      // Sub-graphs use composite IDs: ${parentInstanceId}/${nodeId}
      const subInstanceId = `${instanceId}/${nodeId}`;
      const subInstance = this.instances[subInstanceId];
      if (subInstance) {
        // It's a GraphInstance - use its composite instanceId
        this.loadGraphInstancePreset(subInstanceId, presetName);
      } else {
        // It's a primitive
        this.loadNodePreset(instanceId, nodeId, presetName);
      }
    }
  }

  private loadNodePreset(instanceId: string, nodeId: string, presetName: string): void {
    const instance = this.instances[instanceId];
    if (!instance) {
      logger.error('Runtime', `loadPreset: instance '${instanceId}' not found`);
      return;
    }

    const definition = this.graphDefinitions[instance.definitionId];
    if (!definition) return;

    const nodeType = getNodeType(definition.nodes[nodeId]);
    const nodeDef = this.pluginRegistry.getNodeDef(nodeType);
    const preset = this.pluginRegistry.getPreset(nodeType, presetName);
    if (!preset) {
      logger.error('Runtime', `Preset '${presetName}' not found for node type '${nodeType}'`);
      return;
    }

    const baseEndpoints = nodeDef?.endpoints ?? {};

    // Handle custom params: remove old, create new
    if (nodeDef?.allowAddParam) {
      const nodeConfig = definition.config[nodeId] ?? {};
      const node = instance.nodes[nodeId];

      // Remove existing custom param endpoints from instance (including base endpoints marked custom)
      const existingCustomParams = Object.keys(nodeConfig).filter(id =>
        !baseEndpoints[id] || (baseEndpoints[id] as any).custom
      );
      for (const paramId of existingCustomParams) {
        delete instance.endpoints[`${nodeId}.${paramId}`];
        delete definition.config[nodeId]?.[paramId];
      }

      // Create new custom param endpoints from preset (skip non-custom base endpoints and metadata descriptors)
      const presetCustomParams = Object.entries(preset).filter(
        ([id, value]) => (!baseEndpoints[id] || (baseEndpoints[id] as any).custom) && !isMetadataDescriptor(value)
      );
      for (const [paramId, value] of presetCustomParams) {
        if (!definition.config[nodeId]) definition.config[nodeId] = {};
        const dataType = Array.isArray(value) ? 'array' : (typeof value === 'string' ? 'string' : 'number');
        definition.config[nodeId][paramId] = { value, dataType } as any;

        // Create endpoint on the live instance
        const processed = params({ [paramId]: { value, dataType } as any })[paramId];
        const epConfig = { id: paramId, ...processed, custom: true } as Endpoint;
        const endpoint = this.createEndpoint(instance, node, epConfig);
        instance.endpoints[`${node.id}.${paramId}`] = endpoint;
        this.pushValue(instanceId, nodeId, paramId, value);
      }
    }

    // Apply base endpoint values and metadata (skip custom-flagged, handled above)
    for (const [endpointId, value] of Object.entries(preset)) {
      if (!baseEndpoints[endpointId] || (baseEndpoints[endpointId] as any).custom) continue;

      if (isMetadataDescriptor(value)) {
        // Apply metadata overrides (min, max, step) and optionally set value
        const { value: val, ...metadata } = value;
        for (const [field, fieldValue] of Object.entries(metadata)) {
          this.setEndpointMetadata(instance.definitionId, nodeId, endpointId, field, fieldValue);
        }
        if (val !== undefined) {
          this.applyTweak(instanceId, nodeId, endpointId, val);
        }
      } else {
        this.applyTweak(instanceId, nodeId, endpointId, value);
      }
    }

    // Notify node and push definition for UI
    const node = instance.nodes[nodeId];
    if (node && typeof (node as any).onParamChanged === 'function') {
      (node as any).onParamChanged();
    }
    this.pushDefinition(instance.definitionId);
  }

  private loadGraphInstancePreset(instanceId: string, presetName: string): void {
    const instance = this.instances[instanceId];
    if (!instance) {
      logger.error('Runtime', `loadPreset: instance '${instanceId}' not found`);
      return;
    }

    const graphDef = this.graphDefinitions[instance.definitionId];
    const containingInstanceId = this.containedBy[instanceId];

    loadGraphPreset(graphDef, presetName, (endpointId, value) => {
      if (!containingInstanceId) {
        // Root case: no parent config, update api directly
        if (graphDef.api[endpointId]) {
          graphDef.api[endpointId].value = value;
          const [nodeId, epId] = graphDef.api[endpointId].path.split('.');
          this.pushValue(instanceId, nodeId, epId, value);
        }
        this.pushDefinition(instance.definitionId);
      } else {
        const nodeId = instanceId.split('/').pop()!;
        this.applyTweak(containingInstanceId, nodeId, endpointId, value);
      }
    });
  }

  /**
   * Set endpoint disabled state.
   * Updates the containing definition's config (source of truth).
   */
  setEndpointDisabled(instanceId: string, nodeId: string, endpointId: string, disabled: boolean): void {
    const instance = this.instances[instanceId];
    if (!instance) return;

    const definition = this.graphDefinitions[instance.definitionId];
    if (!definition) return;

    // Update definition config
    if (!definition.config[nodeId]) definition.config[nodeId] = {};
    if (!definition.config[nodeId][endpointId]) definition.config[nodeId][endpointId] = {};
    (definition.config[nodeId][endpointId] as Partial<Endpoint>).disabled = disabled;

    // Notify UI
    this.pushDefinition(instance.definitionId);
  }

  /**
   * Set endpoint metadata by instanceId.
   * Implementation for node endpoint operations.
   */
  setEndpointMetadataByInstance(instanceId: string, nodeId: string, endpointId: string, metadata: Record<string, any>): void {
    const instance = this.instances[instanceId];
    if (!instance) return;

    // Delegate to definitionId-based method
    for (const [field, fieldValue] of Object.entries(metadata)) {
      if (fieldValue !== undefined) this.setEndpointMetadata(instance.definitionId, nodeId, endpointId, field, fieldValue);
    }
  }

  /**
   * Get current endpoint metadata from definition config.
   * Returns endpoint with config overrides applied.
   */
  getEndpointMetadata(instanceId: string, nodeId: string, endpointId: string): Endpoint | undefined {
    const instance = this.instances[instanceId];
    if (!instance) return undefined;

    const definition = this.graphDefinitions[instance.definitionId];
    if (!definition) return undefined;

    // Get base endpoint from plugin
    const nodeType = getNodeType(definition.nodes[nodeId]);
    const endpoints = this.pluginRegistry.getEndpoints(nodeType);
    const baseEndpoint = endpoints?.[endpointId];

    if (!baseEndpoint) {
      // Custom param — already created as a live endpoint
      const liveEndpoint = instance.endpoints[`${nodeId}.${endpointId}`];
      return liveEndpoint as unknown as Endpoint ?? undefined;
    }

    // Get config overrides (cast to Partial<Endpoint> for property access)
    const config = (definition.config[nodeId]?.[endpointId] ?? {}) as Partial<Endpoint>;

    return {
      id: endpointId,
      ...baseEndpoint,
      isOutput: config.isOutput ?? baseEndpoint.isOutput ?? false,
      isInput: config.isInput ?? baseEndpoint.isInput ?? false,
      disabled: config.disabled ?? false,
      min: config.min ?? baseEndpoint.min,
      max: config.max ?? baseEndpoint.max,
      step: config.step ?? baseEndpoint.step,
      options: (config.options ?? baseEndpoint.options) as any[],
      useExp: config.useExp ?? baseEndpoint.useExp ?? false,
      exp: (config as any).exp ?? baseEndpoint.exp,
    };
  }

  /**
   * Set value on internal endpoint via API routing (from exposed endpoint, preset, or tweak).
   */
  setInternalValue(instanceId: string, nodeId: string, endpointId: string, value: any): void {
    const instance = this.instances[instanceId];
    if (!instance) {
      throw new Error(`Instance '${instanceId}' not found`);
    }

    const endpoint = instance.endpoints[`${nodeId}.${endpointId}`];
    if (!endpoint) {
      throw new Error(`Endpoint '${nodeId}.${endpointId}' not found in instance '${instanceId}'`);
    }

    endpoint.setValue(value);
  }

  pushValue(instancePath: string, nodeId: string, endpointId: string, value: any) {
    this.store.setValue({ instancePath, nodeId, endpointId }, value);
  }

  pushDefinition(definitionId: string, newNodeIds?: string[]) {
    if (this.loading) {
      //   logger.trace('Runtime', `[pushDefinition] SKIPPED (loading) ${definitionId}`);
      return;
    }

    // Debug: log caller stack to identify render loop source

    logger.trace('Runtime', `[pushDefinition] ${definitionId}`);

    const definition = this.graphDefinitions[definitionId];
    if (definition) {
      this.store.setDefinition(definitionId, definition, newNodeIds);
    }
  }

  /**
   * Store initial values for all endpoints BEFORE node initialization.
   * This ensures params proxy returns values when nodes read during initialize().
   */
  private storeInitialValues(
    instance: IGraphInstance,
    graphDefinition: GraphDefinition,
    nodeInitData: Record<string, Record<string, any>>
  ): void {
    for (const [nodeId, nodeEntry] of Object.entries(graphDefinition.nodes)) {
      const nodeType = getNodeType(nodeEntry);
      // Ensure node entry exists in store (even if no values)
      this.store.ensureNodeEntry(instance.instancePath, nodeId);

      // Get config data for this node (already collected)
      const configData = nodeInitData[nodeId] ?? {};

      // For plugin nodes, store endpoint defaults
      const baseEndpoints = this.pluginRegistry.getEndpoints(nodeType);
      if (baseEndpoints) {
        // Handle base endpoints
        for (const [epId, ep] of Object.entries(baseEndpoints)) {
          // Priority: config value > endpoint default
          const value = configData[epId] ?? ep.value;
          if (value !== undefined) {
            this.pushValue(instance.instancePath, nodeId, epId, value);
          }
        }
        // Handle dynamic endpoints (config entries with dataType but no base endpoint)
        const nodeConfig = graphDefinition.config[nodeId];


        const customParams = getCustomParams(nodeConfig, baseEndpoints);
        for (const ep of customParams) {
          if (ep.id && ep.value !== undefined) {
            this.pushValue(instance.instancePath, nodeId, ep.id, ep.value);
          }
        }
      } else {
        // For subgraph nodes, store api defaults
        const subGraphDef = this.graphDefinitions[nodeType];
        if (subGraphDef) {
          const nodeConfig = graphDefinition.config[nodeId] ?? {};
          for (const [endpointId, exposedEp] of Object.entries(subGraphDef.api)) {
            const config = nodeConfig[endpointId];
            const value = config !== undefined ? resolveConfigValue(config) : exposedEp.value;
            if (value !== undefined) {
              this.pushValue(instance.instancePath, nodeId, endpointId, value);
            }
          }
        }
      }
    }
  }

  /**
   * Push initial values to store for all endpoints in an instance.
   * Called after node initialization to sync default values to the store.
   */
  private pushInitialValues(instance: IGraphInstance): void {
    for (const [nodeId, node] of Object.entries(instance.nodes)) {
      // Sub-instances handle their own internal values, but we still need to emit
      // values for their exposed (virtual) endpoints on this instance level
      if ('nodes' in node) {
        // Emit values for virtual endpoints on subgraph nodes
        this.pushInitialNodeValues(instance.instancePath, nodeId, node);
        continue;
      }
      this.pushInitialNodeValues(instance.instancePath, nodeId, node);
    }
  }

  /**
   * Emit initial values for a single node's endpoints.
   * Also stores default values from endpoint definitions if not already in store.
   */
  private pushInitialNodeValues(instanceId: string, nodeId: string, _node: INode): void {
    // Get endpoint definitions for this node
    const instance = this.instances[instanceId];
    if (!instance) return;

    const definition = this.graphDefinitions[instance.definitionId];
    if (!definition) return;

    const nodeEntry = definition.nodes[nodeId];
    if (!nodeEntry) return;
    const nodeType = getNodeType(nodeEntry);

    // Get endpoint definitions from plugin or graph definition
    const endpoints = this.pluginRegistry.getEndpoints(nodeType) || {};

    // For sub-graph nodes, use the api as endpoints
    // Priority: parent config override > api default value
    if (!this.pluginRegistry.hasNode(nodeType)) {
      const subGraphDef = this.graphDefinitions[nodeType];
      if (subGraphDef) {
        // Get parent definition's config for this specific node instance
        const nodeConfig = definition.config[nodeId] ?? {};

        for (const [endpointId, exposedEp] of Object.entries(subGraphDef.api)) {
          // Check parent config first, then fall back to api default
          const config = nodeConfig[endpointId];
          const value = config !== undefined ? resolveConfigValue(config) : exposedEp.value;

          if (value !== undefined) {
            // Store value (store notifies UI)
            this.pushValue(instanceId, nodeId, endpointId, value);
          }
        }
      }
      return;
    }

    // For primitive nodes, iterate through endpoint definitions
    for (const [endpointId, ep] of Object.entries(endpoints)) {
      let value = this.store.getValue({ instancePath: instanceId, nodeId, endpointId });

      // If no value in store, use default from endpoint definition
      if (value === undefined && ep.value !== undefined) {
        value = ep.value;
        this.pushValue(instanceId, nodeId, endpointId, value);
      }

      // Note: store.setValue already called above if value was undefined
      // Store notifies UI automatically
    }
  }

  // WHICH ONE ??
  /**
   * Record an error for UI display
   */
  private logError(error: Error, context: { nodeId: string; endpointId: string }): void {
    // Report to logger
    logger.error('Runtime', error.message, `${context.nodeId}.${context.endpointId}`);
  }
  /**
   * Throw an error with optional diagnostic info
   */
  error(message: string, diagnostics?: Record<string, any>): never {
    if (diagnostics) {
      logger.error('Runtime', `${message} - ${JSON.stringify(diagnostics)}`);
    } else {
      logger.error('Runtime', message);
    }
    throw new Error(message);
  }

  /** Log at info level */
  private logInfo(message: string, context?: string): void {
    logger.info('Runtime', message, context);
  }

  /** Log at trace level */
  private logTrace(message: string, context?: string): void {
    logger.trace('Runtime', message, context);
  }

  /** Create base dependencies for a node - all functions pre-bound to node context */
  private createDeps(instanceId: string, nodeId: string, nodeType?: string): BaseDependencies {
    // Get definitionId for metadata operations
    const instance = this.instances[instanceId];
    const definitionId = instance?.definitionId ?? instanceId;

    const getEndpoint = (endpointId) => {
      const inst = this.instances[instanceId];
      const endpoint = inst?.endpoints[`${nodeId}.${endpointId}`];
      if (!endpoint) {
        throw new Error(`Endpoint '${nodeId}.${endpointId}' not found in instance '${instanceId}'`);
      }
      return endpoint;
    }
    const getAudioEndpoint = (endpointId) => {
      const inst = this.instances[instanceId];
      const endpoint = inst?.endpoints[`${nodeId}.${endpointId}`] as AudioEndpoint;
      if (!endpoint) {
        throw new Error(`Endpoint '${nodeId}.${endpointId}' not found in instance '${instanceId}'`);
      }
      if (!endpoint.gainNode) {
        throw new Error(`No gain node on endpoint '${nodeId}.${endpointId}'`);
      }
      return endpoint;
    }
    const getRenderer = () => {
      const renderer = getVideoRenderer(instanceId, nodeId);
      if (!renderer) {
        throw new Error(`No renderer registered for node '${nodeId}' in instance '${instanceId}'`);
      }
      return renderer;
    };
    return {
      transport: this.env.transport,
      audioContext: this.env.audioContext,
      graphIndex: this.graphIndex,
      instancePath: instanceId,
      endpointApi: {
        // List all endpoint IDs for this node
        getEndpointIds: () => {
          const instance = this.instances[instanceId];
          if (!instance) return [];
          const prefix = `${nodeId}.`;
          return Object.keys(instance.endpoints)
            .filter(key => key.startsWith(prefix))
            .map(key => key.slice(prefix.length));
        },
        // Get all custom params as { id: value }
        getCustomParams: () => {
          const instance = this.instances[instanceId];
          if (!instance) return {};
          const prefix = `${nodeId}.`;
          const result: Record<string, any> = {};
          for (const [key, endpoint] of Object.entries(instance.endpoints)) {
            if (key.startsWith(prefix) && (endpoint as any).custom) {
              const epId = key.slice(prefix.length);
              result[epId] = resolveConfigValue(endpoint.getValue());
            }
          }
          return result;
        },
        // any endpoint
        setDisabled: (endpointId: string, disabled: boolean) => this.setEndpointDisabled(instanceId, nodeId, endpointId, disabled),
        getValue: (endpointId: string) => {
          const endpoint = getEndpoint(endpointId);
          const getter = (endpoint as any).get;
          if (typeof getter === 'function') {
            try {
              return getter(this.env.transport.ticks);
            } catch (e) {
              logger.error('getValue', `expression error on ${nodeId}.${endpointId}: ${e}`);
              return resolveConfigValue(endpoint.getValue());
            }
          }
          return resolveConfigValue(endpoint.getValue());
        },
        setValue: (endpointId: string, value: any) => getEndpoint(endpointId).setValue(value),
        sendOutput: (endpointId: string, value: any) => {
          const endpoint = getEndpoint(endpointId)
          // Pass the endpoint itself as source to skip onInput (node is outputting)
          endpoint.setValue(value, endpoint);
        },
        getMetadata: (endpointId: string) => this.getEndpointMetadata(instanceId, nodeId, endpointId),
        setMetadata: (endpointId: string, metadata: Record<string, any>) => {
          for (const [field, fieldValue] of Object.entries(metadata)) {
            if (fieldValue !== undefined) this.setEndpointMetadata(definitionId, nodeId, endpointId, field, fieldValue);
          }
        },
        hasFunctionParams: () => {
          const instance = this.instances[instanceId];
          if (!instance) return false;
          const prefix = `${nodeId}.`;
          for (const [key, endpoint] of Object.entries(instance.endpoints)) {
            if (key.startsWith(prefix) && (endpoint as any).get) {
              return true;
            }
          }
          return false;
        },
        hasGetter: (endpointId: string) => {
          const endpoint = getEndpoint(endpointId);
          return !!(endpoint as any).get;
        },
        setGetFactory: (endpointId: string, factory: GetterFactory | null) => {
          const endpoint = getEndpoint(endpointId);
          const inst = this.instances[instanceId]!;
          (endpoint as any).configGetFactory = factory;
          (endpoint as any).bindGetter(this.env.transport.PPQ, this.env.transport.beatsPerBar,
            getSiblingEndpoints(inst, nodeId, endpointId), () => this.env.transport.ticks);
        },
        connectToInput: (endpointId: string, destination: any) => getAudioEndpoint(endpointId).gainNode.connect(destination),
        connectToOutput: (endpointId: string, source: any) => source.connect(getAudioEndpoint(endpointId).gainNode),
        disconnectFromInput: (endpointId: string, destination: any) => getAudioEndpoint(endpointId).gainNode.disconnect(destination),
        disconnectFromOutput: (endpointId: string, source: any) => source.disconnect(getAudioEndpoint(endpointId).gainNode),
        getGainNode: (endpointId: string) => getAudioEndpoint(endpointId).gainNode,
        getRenderer,
        registerRenderer: (renderer: ICanvasRenderer) => registerVideoRenderer(instanceId, nodeId, renderer),
        unregisterRenderer: () => unregisterVideoEntry(instanceId, nodeId),
        addCustomParam: (endpointId: string, config?) => {
          this.addEndpoint(definitionId, nodeId, endpointId, (config ?? {}) as Partial<Endpoint>);
        },
        removeCustomParam: (endpointId: string) => {
          this.removeEndpoint(definitionId, nodeId, endpointId);
        },
        addEndpoint: (endpointId: string, config: Endpoint) => {
          this.addEndpoint(definitionId, nodeId, endpointId, config);
        },
        removeEndpoint: (endpointId: string) => {
          this.removeEndpoint(definitionId, nodeId, endpointId);
        },
        getNodeApi: (targetNodeId: string) => {
          const instance = this.instances[instanceId];
          if (!instance) return undefined;
          const prefix = `${targetNodeId}.`;
          const result: Record<string, Endpoint> = {};
          let found = false;
          for (const [key, endpoint] of Object.entries(instance.endpoints)) {
            if (key.startsWith(prefix)) {
              const epId = key.slice(prefix.length);
              result[epId] = this.getEndpointMetadata(instanceId, targetNodeId, epId) ?? (endpoint as any);
              found = true;
            }
          }
          return found ? result : undefined;
        },
      },
      ...(nodeType?.split('.').pop() === 'Repeater' ? { repeaterApi: this.createRepeaterApi(instanceId, nodeId) } : {})
    };
  }

  /**
   * Create RepeaterApi for a Repeater node — pre-bound closures for child instance management.
   */
  private createRepeaterApi(parentInstanceId: string, repeaterNodeId: string) {
    const runtime = this;
    const getDefinitionId = () => {
      const inst = runtime.instances[parentInstanceId];
      return inst?.definitionId ?? parentInstanceId;
    };

    return {
      createChildInstance(definitionId: string, childInstanceId: string): void {
        // Guard against recursive nesting
        const targetDef = runtime.graphDefinitions[definitionId];
        if (targetDef) {
          for (const nodeEntry of Object.values(targetDef.nodes)) {
            if (getNodeType(nodeEntry) === 'Repeater') {
              throw new Error(`Cannot nest Repeater inside target definition '${definitionId}'`);
            }
          }
        }

        runtime.containedBy[childInstanceId] = parentInstanceId;
        runtime.instantiate(definitionId, childInstanceId);

        // Register subgraph edges so the scene builder can walk from
        // the repeater's mirror outputs into each child's internal pipeline
        const childTargetDef = runtime.graphDefinitions[definitionId];
        if (childTargetDef) {
          for (const [exposedName, ep] of Object.entries(childTargetDef.api)) {
            const [internalNodeId, internalEndpointId] = ep.path.split('.');
            runtime.graphIndex.addSubgraphEdge({
              parentInstancePath: parentInstanceId,
              subNodeId: repeaterNodeId,
              subInstancePath: childInstanceId,
              internalNodeId,
              exposedName,
              internalEndpointId,
              isOutput: !!ep.isOutput,
            });
          }
        }
      },

      disposeChildInstance(childInstanceId: string): void {
        if (runtime.instances[childInstanceId]) {
          runtime.disposeInstance(childInstanceId);
        }
      },

      setChildValue(childInstanceId: string, endpointId: string, value: any): void {
        const childInstance = runtime.instances[childInstanceId];
        if (!childInstance) return;

        const childDef = runtime.graphDefinitions[childInstance.definitionId];
        if (!childDef) return;

        const exposed = childDef.api[endpointId];
        if (!exposed) {
          if (endpointId === 'posX' || endpointId === 'posZ') {
            logger.trace(`[setChildValue] MISS: ${childInstanceId} ${endpointId} not in API (keys: ${Object.keys(childDef.api).join(',')})`);
          }
          return;
        }

        const [internalNodeId, internalEndpointId] = exposed.path.split('.');
        if (endpointId === 'posX' || endpointId === 'posZ') {
          logger.trace(`[setChildValue] ${childInstanceId} ${endpointId}=${value} → ${internalNodeId}.${internalEndpointId}`);
        }
        runtime.setInternalValue(childInstanceId, internalNodeId, internalEndpointId, value);
      },

      getChildValue(childInstanceId: string, endpointId: string): any {
        const childInstance = runtime.instances[childInstanceId];
        if (!childInstance) return undefined;

        const childDef = runtime.graphDefinitions[childInstance.definitionId];
        if (!childDef) return undefined;

        const exposed = childDef.api[endpointId];
        if (!exposed) return undefined;

        const [internalNodeId, internalEndpointId] = exposed.path.split('.');
        const ep = childInstance.endpoints[`${internalNodeId}.${internalEndpointId}`];
        return ep?.getValue();
      },

      getDefinitionApi(definitionId: string) {
        return runtime.graphDefinitions[definitionId]?.api;
      },

      syncMirrorEndpoints(targetDefinitionId: string, preserveIds?: Set<string>): void {
        const targetDef = runtime.graphDefinitions[targetDefinitionId];
        if (!targetDef) return;

        const definitionId = getDefinitionId();

        // Helper: resolve internal endpoint metadata from definition + plugin registry
        const getInternalEp = (ep: { path: string }) => {
          const [intNodeId, intEpId] = ep.path.split('.');
          const nodeEntry = targetDef.nodes[intNodeId];
          if (!nodeEntry) return undefined;
          const nodeType = getNodeType(nodeEntry);
          const baseEndpoints = runtime.pluginRegistry.getEndpoints(nodeType);
          const baseEp = baseEndpoints?.[intEpId];
          if (!baseEp) return undefined;
          // Merge with config overrides
          const config = (targetDef.config[intNodeId]?.[intEpId] ?? {}) as any;
          return {
            ...baseEp,
            isOutput: config.isOutput ?? baseEp.isOutput ?? false,
            isInput: config.isInput ?? baseEp.isInput ?? false,
            value: config.value ?? baseEp.value,
            dataType: baseEp.dataType,
            min: config.min ?? baseEp.min,
            max: config.max ?? baseEp.max,
            step: config.step ?? baseEp.step,
          };
        };

        // Determine which mirror endpoints should exist (inputs AND outputs)
        const desired = new Set<string>();
        const outputEndpoints = new Map<string, { dataType: string }>();
        for (const [name, ep] of Object.entries(targetDef.api)) {
          const intEp = getInternalEp(ep);
          desired.add(name);
          if (intEp?.isOutput && !intEp?.isInput) {
            outputEndpoints.set(name, { dataType: intEp.dataType });
          }
        }

        // Get current custom params on the repeater node
        const instance = runtime.instances[parentInstanceId];
        if (!instance) return;
        const prefix = `${repeaterNodeId}.`;
        const currentCustom = new Set<string>();
        for (const key of Object.keys(instance.endpoints)) {
          if (key.startsWith(prefix)) {
            const ep = instance.endpoints[key];
            if ((ep as any).custom) {
              currentCustom.add(key.slice(prefix.length));
            }
          }
        }

        // Remove stale mirror endpoints (but preserve adapter transform endpoints)
        for (const epId of currentCustom) {
          if (!desired.has(epId) && !preserveIds?.has(epId)) {
            runtime.removeEndpoint(definitionId, repeaterNodeId, epId);
          }
        }

        // Add or update mirror endpoints
        for (const name of desired) {
          const ep = targetDef.api[name];
          const outInfo = outputEndpoints.get(name);

          if (currentCustom.has(name)) {
            // Endpoint exists — update dataType if it changed (e.g., stale 'any' from SwitchNode)
            if (outInfo) {
              const def = runtime.graphDefinitions[definitionId];
              const existing = def?.config[repeaterNodeId]?.[name] as any;
              if (existing && existing.dataType !== outInfo.dataType) {
                existing.dataType = outInfo.dataType;
              }
            }
            continue;
          }

          if (outInfo) {
            // Output endpoint: mirror with actual dataType, internal dataFlow
            runtime.addEndpoint(definitionId, repeaterNodeId, name, {
              value: undefined,
              dataType: outInfo.dataType as any,
              dataFlow: 'internal',
              isOutput: true,
              label: ep.label ?? name,
            });
          } else {
            // Input endpoint: mirror as array for fan-out distribution.
            // Per-instance params (with options): default to the options array.
            const initialValue = ep.options?.length ? [...ep.options] : [];
            runtime.addEndpoint(definitionId, repeaterNodeId, name, {
              value: initialValue,
              dataType: 'array',
              label: ep.label ?? name,
              group: 'Instance Values',
            });
          }
        }
      },

      animateChild(childInstanceId: string): void {
        runtime.instances[childInstanceId]?.animate();
      },

      childIds: [] as string[],
      _prevLoading: false,

      beginBatch(): void {
        this._prevLoading = runtime.loading;
        runtime.loading = true;
        runtime.store.beginBatch();
      },

      endBatch(): void {
        const _bt0 = performance.now();
        runtime.loading = this._prevLoading;
        runtime.store.endBatch();
        const _bt1 = performance.now();
        if (!runtime.loading) {
          // Emit values for the parent instance only (contains the repeater node output)
          runtime.store.emitValuesChanged(parentInstanceId);
          const _bt2 = performance.now();
          // Push one definition per unique type, plus the parent
          const affectedDefs = new Set<string>();
          for (const childId of this.childIds) {
            const inst = runtime.instances[childId];
            if (inst) affectedDefs.add(inst.definitionId);
          }
          const parentDef = runtime.instances[parentInstanceId]?.definitionId;
          if (parentDef) affectedDefs.add(parentDef);
          for (const defId of affectedDefs) {
            runtime.pushDefinition(defId);
          }
          const _bt3 = performance.now();
          logger.info('RepeaterApi', `endBatch: storeBatch=${(_bt1-_bt0).toFixed(1)}ms emit=${(_bt2-_bt1).toFixed(1)}ms pushDefs=${(_bt3-_bt2).toFixed(1)}ms total=${(_bt3-_bt0).toFixed(1)}ms`);
        }
      },

      getAdapter(outputDataTypes: string[]): RepeaterDomainAdapter | undefined {
        return runtime.getRepeaterAdapter(outputDataTypes);
      },

      getDefinitionOutputTypes(definitionId: string): string[] {
        const def = runtime.graphDefinitions[definitionId];
        if (!def) return [];
        const types: string[] = [];
        for (const [, ep] of Object.entries(def.api)) {
          if (!ep.isOutput) continue;
          // Resolve internal endpoint dataType
          const [intNodeId, intEpId] = ep.path.split('.');
          const nodeEntry = def.nodes[intNodeId];
          if (!nodeEntry) continue;
          const nodeType = getNodeType(nodeEntry);
          const baseEndpoints = runtime.pluginRegistry.getEndpoints(nodeType);
          const baseEp = baseEndpoints?.[intEpId];
          let dt = baseEp?.dataType;
          // If base type is 'any', check config for a more specific type
          // (e.g. SwitchIn with configured dataType, or config override on the endpoint)
          if (dt === 'any' || !dt) {
            const configDt = (def.config?.[intNodeId]?.[intEpId] as any)?.dataType;
            if (configDt && configDt !== 'any') dt = configDt;
          }
          if (dt) types.push(dt);
        }
        return types;
      },

      getSiblingValue(siblingNodeId: string, endpointId: string): any {
        const instance = runtime.instances[parentInstanceId];
        if (!instance) return undefined;
        const key = `${siblingNodeId}.${endpointId}`;
        const ep = instance.endpoints[key];
        if (!ep) return undefined;
        const payload = ep.getValue();
        return payload?.value ?? payload;
      },
    };
  }

  // ─────────────────────────────────────────────────────────────
  // Plugin Registration
  // ─────────────────────────────────────────────────────────────

  /**
   * Register a plugin with the runtime.
   * Plugins provide endpoint types and/or node definitions.
   */
  registerPlugin(plugin: Plugin): void {
    this.pluginRegistry.registerPlugin(plugin);
  }

  /**
   * Unregister a plugin from the runtime.
   */
  unregisterPlugin(pluginId: string): void {
    this.pluginRegistry.unregisterPlugin(pluginId);
  }

  /** Register a domain adapter for template-based Repeater rendering */
  registerRepeaterAdapter(adapter: RepeaterDomainAdapter): void {
    for (const type of adapter.outputTypes) {
      this.repeaterAdapters.set(type, adapter);
    }
  }

  /** Get a domain adapter matching the given output types (queries plugins lazily) */
  getRepeaterAdapter(outputDataTypes: string[]): RepeaterDomainAdapter | undefined {
    // Check cached adapters first
    for (const dt of outputDataTypes) {
      const adapter = this.repeaterAdapters.get(dt);
      if (adapter) return adapter;
    }
    // Query plugins for adapters (lazy registration — e.g. after async three.js load)
    for (const plugin of this.pluginRegistry.getAllPlugins()) {
      if (!plugin.getRepeaterAdapters) continue;
      for (const adapter of plugin.getRepeaterAdapters()) {
        for (const type of adapter.outputTypes) {
          this.repeaterAdapters.set(type, adapter);
        }
        // Check if this adapter matches
        for (const dt of outputDataTypes) {
          if (adapter.outputTypes.includes(dt)) return adapter;
        }
      }
    }
    return undefined;
  }

  /**
   * Get the plugin registry.
   */
  getPluginRegistry(): PluginRegistry {
    return this.pluginRegistry;
  }

  // ─────────────────────────────────────────────────────────────
  // Graph Management
  // ─────────────────────────────────────────────────────────────

  registerGraphDefinition(graphDefinition: GraphDefinition): void {
    // Ensure required fields exist
    if (!graphDefinition.nodes) graphDefinition.nodes = {};
    if (!graphDefinition.connections) graphDefinition.connections = [];
    if (!graphDefinition.api) graphDefinition.api = {}; // not relevant for 'root' definition
    if (!graphDefinition.config) graphDefinition.config = {};

    // Register any nested definitions first
    if (graphDefinition.definitions) {
      for (const nestedDef of Object.values(graphDefinition.definitions)) {
        this.registerGraphDefinition(nestedDef);
      }
    }

    this.graphDefinitions[graphDefinition.id] = graphDefinition;
    if (!this.instancesByDefinition[graphDefinition.id]) {
      this.instancesByDefinition[graphDefinition.id] = new Set();
    }

    this.pushDefinition(graphDefinition.id);
  }

  /**
   * Rebuild the runtime after a definition change.
   * Disposes all instances and re-instantiates from current definitions.
   */
  rebuildFromDefinitions(): void {
    this.logTrace(`[rebuildFromDefinitions] Rebuilding runtime from definitions`);

    // Use loadProject which properly disposes and recreates everything
    this.loadProject(this.graphDefinitions);
  }

  getGraphDefinition(definitionId: string): GraphDefinition | undefined {
    return this.graphDefinitions[definitionId];
  }

  getAllGraphDefinitions(): Record<string, GraphDefinition> {
    return this.graphDefinitions;
  }




  /**
   * Get the definition ID for an instance
   */
  getInstanceDefinitionId(instanceId: string): string | undefined {
    return this.instances[instanceId]?.definitionId;
  }

  /**
   * Get effective endpoint metadata for a node in a definition.
   * Combines base primitive endpoint config with definition config overrides.
   */
  getEndpoint(definitionId: string, nodeId: string, endpointId: string): Endpoint | undefined {
    const definition = this.graphDefinitions[definitionId];
    if (!definition) return undefined;

    const nodeEntry = definition.nodes[nodeId];
    if (!nodeEntry) return undefined;
    const nodeType = getNodeType(nodeEntry);

    // Get base endpoint from plugin
    const endpoints = this.pluginRegistry.getEndpoints(nodeType);
    if (!endpoints) {
      // It's a sub-graph - trace through to the internal endpoint
      const subDef = this.graphDefinitions[nodeType];
      if (!subDef) return undefined;
      const exposedEndpoint = subDef.api[endpointId];
      if (!exposedEndpoint) return undefined;

      // Parse the internal path to get the actual endpoint type
      const [internalNodeId, internalEndpointId] = exposedEndpoint.path.split('.');
      const internalNodeType = getNodeType(subDef.nodes[internalNodeId]);
      const internalEndpoints = this.pluginRegistry.getEndpoints(internalNodeType);
      const internalEndpoint = internalEndpoints?.[internalEndpointId];

      return {
        id: endpointId,
        label: exposedEndpoint.label ?? endpointId,
        dataType: internalEndpoint?.dataType ?? exposedEndpoint.dataType,
        dataFlow: internalEndpoint?.dataFlow,
        isInput: exposedEndpoint.isInput,
        isOutput: exposedEndpoint.isOutput,
        min: exposedEndpoint.min,
        max: exposedEndpoint.max,
        step: exposedEndpoint.step,
        value: exposedEndpoint.value,
        ...(toConfigObject(definition.config[nodeId]?.[endpointId]))
      } as Endpoint;
    }

    const baseEndpoint = endpoints[endpointId];
    if (!baseEndpoint) {
      // Check for custom param in config (dynamic params added via allowAddParam)
      const configEntry = definition.config[nodeId]?.[endpointId];
      if (configEntry && typeof configEntry === 'object' && !Array.isArray(configEntry)) {
        const processed = (configEntry as any).dataFlow === 'internal'
          ? internals({ [endpointId]: configEntry })[endpointId]
          : params({ [endpointId]: configEntry })[endpointId];
        return { id: endpointId, ...processed, custom: true } as Endpoint;
      }
      return undefined;
    }

    // Apply config overrides from the containing definition
    const configOverrides = toConfigObject(definition.config[nodeId]?.[endpointId]);

    return { id: endpointId, ...baseEndpoint, ...configOverrides, options: ((configOverrides as Partial<Endpoint>).options ?? baseEndpoint.options) as any[] };
  }

  /**
   * Set a config value directly on a definition (no runtime propagation)
   * Used for endpoint config
   */
  /* setConfigValue(definitionId: string, nodeId: string, endpointId: string, field: string, value: any): void {
     const def = this.graphDefinitions[definitionId];
     if (!def) {
       throw new Error(`setConfigValue: definition '${definitionId}' not found`);
     }
 
     if (!def.config[nodeId]) {
       def.config[nodeId] = {};
     }
     if (!def.config[nodeId][endpointId]) {
       def.config[nodeId][endpointId] = {};
     }
 
     def.config[nodeId][endpointId][field] = value;
 
   }*/

  /**
   * Update internal config on a definition (propagates to all instances)
   */
  setGraphConfig(definitionId: string, nodeId: string, endpointId: string, field: string, value: any): void {
    const definition = this.graphDefinitions[definitionId];
    if (!definition) {
      throw new Error(`Definition '${definitionId}' not found`);
    }

    // Verify the node exists in the definition
    const nodeEntry = definition.nodes[nodeId];
    if (!nodeEntry) {
      throw new Error(`Node '${nodeId}' not found in definition '${definitionId}'`);
    }
    const nodeType = getNodeType(nodeEntry);

    // Check if this is a sub-graph node
    const isSubgraph = !this.pluginRegistry.hasNode(nodeType);

    // For sub-graph nodes, metadata changes go to the sub-graph's definition API
    // (shared across all instances). Value changes go to containing def's config (per-instance).
    if (isSubgraph && field !== 'value' && field !== 'useExp' && field !== 'exp' && field !== 'tweakable') {
      const subgraphDef = this.graphDefinitions[nodeType];
      if (subgraphDef?.api[endpointId]) {
        subgraphDef.api[endpointId][field] = value;
        // push sub-graph definition (affects all uses of this sub-graph)
        this.pushDefinition(nodeType);
        // also push containing definition (UI needs to update)
        this.pushDefinition(definitionId);
        return;
      }
    }

    // For primitives (or sub-graph value changes): update containing definition's config
    if (!definition.config[nodeId]) definition.config[nodeId] = {};
    const existing = definition.config[nodeId][endpointId];
    // Config can be compact (raw value) or full ({value, ...}). Ensure it's an object before setting field.
    if (!existing || typeof existing !== 'object' || Array.isArray(existing)) {
      definition.config[nodeId][endpointId] = { value: existing };
    }
    definition.config[nodeId][endpointId][field] = value;

    // Propagate to all instances of this definition
    const instanceIds = this.instancesByDefinition[definitionId];
    if (!instanceIds || instanceIds.size === 0) return;

    for (const instanceId of instanceIds) {
      this.setEndpointFromConfig(instanceId, nodeId, endpointId, field, value);
    }

    // Emit nodesUpdated for metadata changes (value changes emit via setValue)
    if (field !== 'value') {
      this.pushDefinition(definitionId);
    }
  }

  /**
   * Add a custom param to a node's config.
   * Creates the config entry and endpoint on all instances.
   */
  /**
   * Add an endpoint to a node's config and create it on all instances.
   * Used by Switch (dynamic ports), Repeater (mirror endpoints).
   */
  addEndpoint(definitionId: string, nodeId: string, endpointId: string, config: Partial<Endpoint>): void {
    const definition = this.graphDefinitions[definitionId];
    if (!definition) {
      throw new Error(`Definition '${definitionId}' not found`);
    }

    // Write to definition config so buildNodeState picks it up
    if (!definition.config[nodeId]) definition.config[nodeId] = {};
    definition.config[nodeId][endpointId] = {
      value: config.value ?? 0,
      ...(config.dataType && { dataType: config.dataType }),
      ...(config.dataFlow && { dataFlow: config.dataFlow }),
      ...(config.label && { label: config.label }),
      ...(config.isInput !== undefined && { isInput: config.isInput }),
      ...(config.isOutput !== undefined && { isOutput: config.isOutput }),
      ...(config.options && { options: config.options }),
      ...(config.group && { group: config.group }),
      ...(config.min !== undefined && { min: config.min }),
      ...(config.max !== undefined && { max: config.max }),
      ...(config.step !== undefined && { step: config.step }),
      custom: true,
    } as Partial<Endpoint>;

    // Create endpoint on all instances — use internals() for internal dataFlow, params() otherwise
    const raw = definition.config[nodeId][endpointId] as any;
    const processed = config.dataFlow === 'internal'
      ? internals({ [endpointId]: raw })[endpointId]
      : params({ [endpointId]: raw })[endpointId];
    const epConfig = { id: endpointId, ...processed, custom: true } as Endpoint;

    const instanceIds = this.instancesByDefinition[definitionId];
    if (instanceIds) {
      for (const instanceId of instanceIds) {
        const instance = this.instances[instanceId];
        if (instance) {
          const node = instance.nodes[nodeId];
          if (node) {
            const endpoint = this.createEndpoint(instance, node, epConfig);
            instance.endpoints[`${node.id}.${endpointId}`] = endpoint;
            this.pushValue(instanceId, nodeId, endpointId, config.value ?? 0);
          }
        }
      }
    }

    this.pushDefinition(definitionId);
  }

  /**
   * Remove an endpoint from a node's config and all instances.
   */
  removeEndpoint(definitionId: string, nodeId: string, endpointId: string): void {
    const definition = this.graphDefinitions[definitionId];
    if (!definition) {
      throw new Error(`Definition '${definitionId}' not found`);
    }

    // Remove from config
    if (definition.config[nodeId]?.[endpointId]) {
      delete definition.config[nodeId][endpointId];
      if (Object.keys(definition.config[nodeId]).length === 0) {
        delete definition.config[nodeId];
      }
    }

    // Remove endpoint from all instances
    const instanceIds = this.instancesByDefinition[definitionId];
    if (instanceIds) {
      for (const instId of instanceIds) {
        const inst = this.instances[instId];
        if (inst) {
          const key = `${nodeId}.${endpointId}`;
          const ep = inst.endpoints[key];
          if (ep) {
            ep.dispose();
            delete inst.endpoints[key];
          }
        }
      }
    }

    this.pushDefinition(definitionId);
  }

  /**
   * Apply a graph config value to a single instance's node
   * Called by setGraphConfig which loops through all instances
   * NOTE: For value changes, applies directly without calling applyTweak (to avoid recursion)
   */
  private setEndpointFromConfig(instanceId: string, nodeId: string, endpointId: string, field: string, value: any): void {
    const instance = this.instances[instanceId];
    if (!instance) {
      throw new Error(`Instance '${instanceId}' not found`);
    }

    const node = instance.nodes[nodeId];
    if (!node) {
      throw new Error(`Node '${nodeId}' not found in instance '${instanceId}'. Available nodes: ${Object.keys(instance.nodes).join(', ')}`);
    }

    if (field === 'value') {
      // Intercept preset endpoint - load preset then store the value
      if (endpointId === 'preset') {
        this.loadPreset(instanceId, nodeId, value);
        // Fall through to set the preset endpoint value so the UI reflects the selection
      }

      // All endpoints (primitives and sub-graph exposed) are on the containing instance
      const endpoint = instance.endpoints[`${nodeId}.${endpointId}`];
      if (endpoint) {
        endpoint.setValue(value);
      } else {
        throw new Error(`Endpoint '${nodeId}.${endpointId}' not found for tweak in instance '${instanceId}'`);
      }
    } else {
      // Recompile getter when useExp or exp source changes
      if (field === 'useExp' || field === 'exp') {
        const definition = this.graphDefinitions[instance.definitionId];
        const epConfig = definition?.config[nodeId]?.[endpointId] ?? {};
        const useExpr = (epConfig as any).useExp;
        const exprSrc = (epConfig as any).exp;
        const compiled = useExpr ? compileGetter(exprSrc) : null;
        const endpoint = instance.endpoints[`${nodeId}.${endpointId}`];
        if (endpoint) {
          (endpoint as any).configGetFactory = compiled;
          (endpoint as any).bindGetter(this.env.transport.PPQ, this.env.transport.beatsPerBar, getSiblingEndpoints(instance, nodeId, endpointId), () => this.env.transport.ticks);
          if (useExpr) {
            // Propagate getter to connections without evaluating it (preserve stored value)
            const stored = this.store.getPayload(endpoint.storeKey);
            endpoint.setValue(resolveConfigValue(stored));
          } else {
            // Restore value and strip stale get from store
            const configValue = (epConfig as any).value;
            const stored = this.store.getPayload(endpoint.storeKey);
            endpoint.setValue(configValue !== undefined ? configValue : resolveConfigValue(stored));
          }
        }
        // Bump graph version so SceneNode re-realizes with updated animate callbacks
        this.graphIndex.touch();
      }
    }
  }

  setEndpointMetadata(definitionId: string, nodeId: string, endpointId: string, field: string, value: any) {
    try {
      if (field === 'value') {
        throw new Error('value is not metadata')
      }

      // Update definition config (source of truth for endpoint metadata)
      const definition = this.graphDefinitions[definitionId];
      if (!definition) {
        throw new Error(`Definition '${definitionId}' not found`);
      }

      if (!definition.config[nodeId]) definition.config[nodeId] = {};
      if (!definition.config[nodeId][endpointId]) definition.config[nodeId][endpointId] = {};
      definition.config[nodeId][endpointId][field] = value;

      // Recompile getter when useExp or exp source changes
      if (field === 'useExp' || field === 'exp') {
        const epConfig = definition.config[nodeId]?.[endpointId] ?? {};
        const useExpr = (epConfig as any).useExp;
        const exprSrc = (epConfig as any).exp;
        const compiled = useExpr ? compileGetter(exprSrc) : null;
        const instanceIds = this.instancesByDefinition[definitionId];
        if (instanceIds) {
          for (const instanceId of instanceIds) {
            const inst = this.instances[instanceId];
            const endpoint = inst?.endpoints[`${nodeId}.${endpointId}`];
            if (endpoint && inst) {
              (endpoint as any).configGetFactory = compiled;
              (endpoint as any).bindGetter(this.env.transport.PPQ, this.env.transport.beatsPerBar, getSiblingEndpoints(inst, nodeId, endpointId), () => this.env.transport.ticks);
              if (useExpr) {
                // Propagate getter to connections without evaluating it (preserve stored value)
                const stored = this.store.getPayload(endpoint.storeKey);
                endpoint.setValue(resolveConfigValue(stored));
              } else {
                // Restore value and strip stale get from store
                const configValue = (epConfig as any).value;
                const stored = this.store.getPayload(endpoint.storeKey);
                endpoint.setValue(configValue !== undefined ? configValue : resolveConfigValue(stored));
              }
            }
          }
        }
        // Bump graph version so SceneNode re-realizes with updated animate callbacks
        this.graphIndex.touch();
      }

      this.pushDefinition(definitionId);

    } catch (e) {
      logger.error('error setting endpoint metadata')
    }
  }

  /**
   * Expose an endpoint on a definition.
   * Updates the definition's api and rebuilds virtual endpoints on all running instances.
   * Note: Input/output direction is configured per-instance via config, not here.
   */
  /**
   * Find all definitions that use a given definition as a node type.
   */
  private getParentDefinitions(definitionId: string): string[] {
    const parents: string[] = [];
    for (const [parentId, parentDef] of Object.entries(this.graphDefinitions)) {
      if (parentId === definitionId) continue;
      for (const nodeEntry of Object.values(parentDef.nodes)) {
        if (getNodeType(nodeEntry) === definitionId) {
          parents.push(parentId);
          break;
        }
      }
    }
    return parents;
  }

  exposeEndpoint(definitionId: string, internalPath: string): void {
    const definition = this.graphDefinitions[definitionId];
    if (!definition) {
      throw new Error(`Definition '${definitionId}' not found`);
    }

    // Check if this path is already exposed
    const alreadyExposed = Object.values(definition.api).some(e => e.path === internalPath);
    if (alreadyExposed) {
      throw new Error(`Endpoint '${internalPath}' is already exposed on definition '${definitionId}'.`);
    }

    // Get the internal endpoint to determine default input/output
    const [nodeId, endpointId] = internalPath.split('.');
    const internalEndpoint = this.getEndpoint(definitionId, nodeId, endpointId);
    if (!internalEndpoint) {
      throw new Error(`Internal endpoint not found: ${internalPath}`);
    }

    // Determine defaults based on internal endpoint
    const { isInput, isOutput } = getExposedEndpointDefaults(internalEndpoint);

    // Get current value from a running instance, or fall back to schema default.
    let value: any = undefined;
    const instanceIds = this.getInstancesOfGraph(definitionId);
    if (instanceIds.length > 0) {
      const instance = this.instances[instanceIds[0]];
      const endpoint = instance?.endpoints[`${nodeId}.${endpointId}`];
      if (endpoint) {
        value = resolveConfigValue(endpoint.getValue());
      }
    }
    if (value === undefined) {
      value = internalEndpoint.value;
    }

    // Generate unique exposed name - start with endpoint id, add 2, 3, etc. if needed
    let exposedName = endpointId;
    let counter = 2;
    while (definition.api[exposedName]) {
      exposedName = `${endpointId}${counter}`;
      counter++;
    }

    definition.api[exposedName] = {
      path: internalPath,
      dataType: internalEndpoint.dataType,
      dataFlow: internalEndpoint.dataFlow,
      label: internalEndpoint.label,
      info: internalEndpoint.info,
      group: internalEndpoint.group,
      isInput,
      isOutput,
      ...(internalEndpoint.min != null ? { min: internalEndpoint.min } : {}),
      ...(internalEndpoint.max != null ? { max: internalEndpoint.max } : {}),
      ...(internalEndpoint.step != null ? { step: internalEndpoint.step } : {}),
      ...(value !== undefined ? { value } : {}),
      ...(internalEndpoint.options ? { options: [...internalEndpoint.options] } : {}),
      ...(internalEndpoint.optionLabels ? { optionLabels: [...internalEndpoint.optionLabels] } : {}),
      ...(internalEndpoint.optionInfo ? { optionInfo: [...internalEndpoint.optionInfo] } : {}),
      ...(internalEndpoint.scaleIncomingValues != null ? { scaleIncomingValues: internalEndpoint.scaleIncomingValues } : {}),
    };

    // Add endpoint to all running instances of this definition
    const newConfig = this.buildEndpoint(definition, exposedName);
    const [internalNodeId, internalEndpointId] = internalPath.split('.');

    for (const instance of Object.values(this.instances)) {
      if (instance.definitionId === definitionId) {
        // This instance IS the definition - add its own exposed endpoint
        this.addExposedEndpoint(instance, newConfig);

        // Wire up exposed endpoint to internal endpoint
        const exposed = instance.endpoints[exposedName] as IEndpoint;
        const internal = instance.endpoints[`${internalNodeId}.${internalEndpointId}`];
        if (exposed && internal) {
          exposed.connectInternal(internal);
          internal.connectTo(exposed);
        }
      }
      for (const [subNodeId, node] of Object.entries(instance.nodes)) {
        if ('definitionId' in node && (node as any).definitionId === definitionId) {
          // This node is a sub-graph of the target definition
          // Add endpoint to CONTAINING instance, not to node
          this.addNodeEndpoint_(instance, node, newConfig);

          // Wire up exposed endpoint to internal endpoint
          const exposed = instance.endpoints[`${node.id}.${exposedName}`] as IEndpoint;
          const subInstance = node as GraphInstance;
          const internal = subInstance.endpoints[`${internalNodeId}.${internalEndpointId}`];
          if (exposed && internal) {
            exposed.connectInternal(internal);
            if (isOutput) {
              internal.connectTo(exposed);
            }
          }

          // Register internal edge for subgraph boundary traversal (graph index)
          this.graphIndex.addSubgraphEdge({
            parentInstancePath: instance.instancePath,
            subNodeId,
            subInstancePath: subInstance.instancePath,
            internalNodeId,
            exposedName,
            internalEndpointId,
            isOutput
          });
        }
      }
    }

    // Notify RuntimeStore of the definition change
    this.pushDefinition(definitionId);

    // Also notify parent definitions that use this subgraph
    // (their NodeState for this subgraph node needs to be rebuilt)
    for (const parentId of this.getParentDefinitions(definitionId)) {
      this.pushDefinition(parentId);
    }

    // Emit initial value for the newly exposed endpoint on all parent instances
    // This ensures the UI can see the value immediately after exposure
    for (const [instanceId, inst] of Object.entries(this.instances)) {
      for (const [nodeIdInParent, subNode] of Object.entries(inst.nodes)) {
        if ('definitionId' in subNode && subNode.definitionId === definitionId) {
          // This node is an instance of the definition we just modified
          // Get value from INTERNAL location using the subgraph's instance ID
          const subInstanceId = (subNode as GraphInstance).id;
          const value = this.store.getValue({ instancePath: subInstanceId, nodeId, endpointId });
          if (value !== undefined) {
            // Store at exposed location (parent instance, node ID in parent, exposed name)
            // Store notifies UI automatically
            this.pushValue(instanceId, nodeIdInParent, exposedName, value);
          }
        }
      }
    }
  }

  /**
   * Check what would be affected by unexposing an endpoint.
   * Returns affected configs and connections without making changes.
   */
  getUnexposeAffectedItems(definitionId: string, exposedName: string): {
    configs: Array<{ definitionId: string; nodeType: string; nodeId: string; endpoint: string }>;
    connections: Array<{
      definitionId: string;
      source: { nodeType: string; nodeId: string; endpoint: string };
      target: { nodeType: string; nodeId: string; endpoint: string };
    }>;
  } {
    const configs: Array<{ definitionId: string; nodeType: string; nodeId: string; endpoint: string }> = [];
    const connections: Array<{
      definitionId: string;
      source: { nodeType: string; nodeId: string; endpoint: string };
      target: { nodeType: string; nodeId: string; endpoint: string };
    }> = [];

    for (const [parentDefId, parentDef] of Object.entries(this.graphDefinitions)) {
      // Check configs
      for (const [nodeId, nodeType] of Object.entries(parentDef.nodes)) {
        if (nodeType === definitionId && parentDef.config[nodeId]?.[exposedName] !== undefined) {
          configs.push({ definitionId: parentDefId, nodeType, nodeId, endpoint: exposedName });
        }
      }

      // Check connections
      for (const conn of parentDef.connections) {
        const [sourceNodeId, sourceEp] = conn.source.split('.');
        const [targetNodeId, targetEp] = conn.target.split('.');
        const sourceType = getNodeType(parentDef.nodes[sourceNodeId]);
        const targetType = getNodeType(parentDef.nodes[targetNodeId]);

        if (sourceType === definitionId && sourceEp === exposedName) {
          connections.push({
            definitionId: parentDefId,
            source: { nodeType: sourceType, nodeId: sourceNodeId, endpoint: sourceEp },
            target: { nodeType: targetType, nodeId: targetNodeId, endpoint: targetEp }
          });
        }
        if (targetType === definitionId && targetEp === exposedName) {
          connections.push({
            definitionId: parentDefId,
            source: { nodeType: sourceType, nodeId: sourceNodeId, endpoint: sourceEp },
            target: { nodeType: targetType, nodeId: targetNodeId, endpoint: targetEp }
          });
        }
      }
    }

    return { configs, connections };
  }

  /**
   * Unexpose an endpoint from a definition.
   * Updates the definition's api, cleans up parent configs and connections, rebuilds virtual endpoints.
   * Returns IDs of parent definitions that were modified (for syncing).
   */
  unexposeEndpoint(definitionId: string, internalPath: string): string[] {
    const definition = this.graphDefinitions[definitionId];
    if (!definition) {
      throw new Error(`Definition '${definitionId}' not found`);
    }
    const [exposedName, exposedEntry] = Object.entries(definition.api).find(entry => entry[1].path === internalPath) ?? []
    const [internalNodeId, internalEndpointId] = internalPath.split('.');
    const isOutput = exposedEntry?.isOutput ?? false;

    // Remove graph index internal edges for this exposed endpoint
    for (const instance of Object.values(this.instances)) {
      for (const [subNodeId, node] of Object.entries(instance.nodes)) {
        if ('definitionId' in node && (node as any).definitionId === definitionId) {
          const subInstance = node as GraphInstance;
          this.graphIndex.removeSubgraphEdge({
            parentInstancePath: instance.instancePath,
            subNodeId,
            subInstancePath: subInstance.instancePath,
            internalNodeId,
            exposedName,
            internalEndpointId,
            isOutput
          });
        }
      }
    }

    // Deactivate live parent connections referencing the endpoint
    // Must happen BEFORE removing the API entry (deactivateConnection needs it for resolution)
    for (const instance of Object.values(this.instances)) {
      const parentDef = this.graphDefinitions[instance.definitionId];
      if (!parentDef) continue;
      const wired = this.wiredConnections[instance.instancePath];
      if (!wired) continue;
      for (const connKey of [...wired]) {
        const [source, target] = connKey.split('->');
        const [sourceNodeId, sourceEp] = source.split('.');
        const [targetNodeId, targetEp] = target.split('.');
        const sourceType = parentDef.nodes[sourceNodeId] ? getNodeType(parentDef.nodes[sourceNodeId]) : undefined;
        const targetType = parentDef.nodes[targetNodeId] ? getNodeType(parentDef.nodes[targetNodeId]) : undefined;
        if ((sourceType === definitionId && sourceEp === exposedName) ||
            (targetType === definitionId && targetEp === exposedName)) {
          try {
            this.deactivateConnection(instance, parentDef, source, target);
          } catch (e) {
            // Connection may already be partially torn down
          }
        }
      }
    }

    // Remove from the definition's api
    delete definition.api[exposedName];

    // Clean up configs and connections in parent definitions
    const modifiedParents: string[] = [];
    for (const [parentDefId, parentDef] of Object.entries(this.graphDefinitions)) {
      let modified = false;

      // Clean up config values
      for (const [nodeId, nodeEntry] of Object.entries(parentDef.nodes)) {
        if (getNodeType(nodeEntry) === definitionId && parentDef.config[nodeId]?.[exposedName] !== undefined) {
          delete parentDef.config[nodeId][exposedName];
          if (Object.keys(parentDef.config[nodeId]).length === 0) {
            delete parentDef.config[nodeId];
          }
          modified = true;
        }
      }

      // Remove connections to/from this endpoint from definition
      const originalLength = parentDef.connections.length;
      parentDef.connections = parentDef.connections.filter(conn => {
        const [sourceNodeId, sourceEp] = conn.source.split('.');
        const [targetNodeId, targetEp] = conn.target.split('.');
        const sourceType = parentDef.nodes[sourceNodeId] ? getNodeType(parentDef.nodes[sourceNodeId]) : undefined;
        const targetType = parentDef.nodes[targetNodeId] ? getNodeType(parentDef.nodes[targetNodeId]) : undefined;

        if (sourceType === definitionId && sourceEp === exposedName) return false;
        if (targetType === definitionId && targetEp === exposedName) return false;
        return true;
      });
      if (parentDef.connections.length !== originalLength) {
        modified = true;
      }

      if (modified && !modifiedParents.includes(parentDefId)) {
        modifiedParents.push(parentDefId);
      }
    }

    // Remove endpoint from all running instances of this definition
    for (const instance of Object.values(this.instances)) {
      if (instance.definitionId === definitionId) {
        // Instance's own exposed endpoint - key is just the name
        this.removeEndpointFromInstance(instance, exposedName);
      }
      for (const [nodeId, node] of Object.entries(instance.nodes)) {
        if ('definitionId' in node && (node as any).definitionId === definitionId) {
          // Sub-graph node - key includes nodeId prefix
          this.removeEndpointFromInstance(instance, `${nodeId}.${exposedName}`);
        }
      }
    }

    // Notify RuntimeStore of definition changes
    this.pushDefinition(definitionId);

    // Notify ALL parent definitions that use this subgraph
    // (their NodeState for this subgraph node needs to be rebuilt)
    for (const parentId of this.getParentDefinitions(definitionId)) {
      this.pushDefinition(parentId);
    }

    return modifiedParents;
  }

  // ─────────────────────────────────────────────────────────────
  // Encapsulation
  // ─────────────────────────────────────────────────────────────

  /**
   * Encapsulate selected nodes into a new definition.
   * Creates a new definition from the nodes and replaces them with an instance.
   *
   * @param containerDefId The definition containing the nodes (e.g., 'root')
   * @param nodeIds The node IDs to encapsulate
   * @param definitionId Name for the new definition
   * @param newInstanceId Optional name for the replacement instance (defaults to definitionId)
   * @returns The instance ID of the new encapsulated node
   */
  encapsulate(
    definitionId: string,
    nodeIds: string[],
    newDefinitionId: string,
    newInstanceId?: string,
    preserveContainerApi = true
  ): string {

    const containerDef = this.graphDefinitions[definitionId];

    newInstanceId = newInstanceId || newDefinitionId;

    // Run encapsulation (pure function)
    const { newDefinition, modifiedContainerDefinition } = encapsulateNodes(containerDef, {
      nodeIds,
      newDefinitionId,
      newInstanceId,
      preserveContainerApi
    });

    // Generate a label for the new encapsulated node (e.g., "mydefinition1")
    const labelPrefix = newDefinitionId.toLowerCase();
    let maxNum = 0;
    for (const nodeEntry of Object.values(modifiedContainerDefinition.nodes)) {
      const label = typeof nodeEntry === 'string' ? undefined : nodeEntry.label;
      if (label && label.startsWith(labelPrefix)) {
        const numPart = label.slice(labelPrefix.length);
        const num = parseInt(numPart, 10);
        if (!isNaN(num) && num > maxNum) {
          maxNum = num;
        }
      }
    }
    const generatedLabel = `${labelPrefix}${maxNum + 1}`;

    // Add label to the new node entry
    const nodeEntry = modifiedContainerDefinition.nodes[newInstanceId];
    if (typeof nodeEntry === 'object') {
      nodeEntry.label = generatedLabel;
    }

    // Preserve positions: copy encapsulated node positions to the new definition
    const containerPositions = this.store.getPositions(definitionId);
    const encapsulatedPositions: Record<string, { x: number; y: number; isCollapsed?: boolean }> = {};
    let sumX = 0, sumY = 0, posCount = 0;

    // Extract positions for encapsulated nodes and calculate center
    for (const nodeId of nodeIds) {
      const pos = containerPositions[nodeId];
      if (pos) {
        encapsulatedPositions[nodeId] = { ...pos };
        sumX += pos.x;
        sumY += pos.y;
        posCount++;
      }
    }

    if (posCount > 0) {
      // Calculate center of selected nodes
      const centerX = sumX / posCount;
      const centerY = sumY / posCount;

      // Normalize positions relative to center (so subgraph is centered at origin)
      const newDefPositions: Record<string, { x: number; y: number; isCollapsed?: boolean }> = {};
      for (const [nodeId, pos] of Object.entries(encapsulatedPositions)) {
        newDefPositions[nodeId] = {
          x: pos.x - centerX,
          y: pos.y - centerY,
          isCollapsed: pos.isCollapsed
        };
      }
      this.store.setPositions(newDefinitionId, newDefPositions);

      // Update container positions: remove encapsulated nodes, add new node at center
      const updatedContainerPositions = { ...containerPositions };
      for (const nodeId of nodeIds) {
        delete updatedContainerPositions[nodeId];
      }
      updatedContainerPositions[newInstanceId] = { x: centerX, y: centerY };
      this.store.setPositions(definitionId, updatedContainerPositions);
    }

    const containerInstanceIds = this.getInstancesOfGraph(definitionId);
    this.logTrace(`[encapsulate] definitionId=${definitionId}, containerInstanceIds=${containerInstanceIds.join(',')}`);
    const containerInstances = containerInstanceIds.map(id => this.instances[id]);

    // Dispose the nodes being encapsulated from all container instances
    for (let containerInstance of containerInstances) {
      for (const nodeId of nodeIds) {
        const node = containerInstance.nodes[nodeId];
        if (node) {
          // Unwire any audio connections first
          for (const conn of containerDef.connections) {
            const [srcNode, srcEp] = conn.source.split('.');
            const [tgtNode, tgtEp] = conn.target.split('.');
            if (srcNode === nodeId || tgtNode === nodeId) {
              const sourceNode = containerInstance.nodes[srcNode];
              const targetNode = containerInstance.nodes[tgtNode];
              if (sourceNode && targetNode && 'onDisconnectedTo' in sourceNode) {
                try {
                  (sourceNode as any).onDisconnectedTo(srcEp, targetNode, tgtEp);
                } catch (e) {
                  // Ignore disconnect errors
                }
              }
            }
          }
          // Dispose the node
          node.dispose();
          delete containerInstance.nodes[nodeId];
        }
      }
    }

    // Emit nodesRemoved once for the definition (after all instances updated)
    this.pushDefinition(definitionId);

    // Register the new definition
    this.registerGraphDefinition(newDefinition);

    // Notify UI of the new definition (including its positions)
    //this.pushDefinition(newDefinitionId);

    // Update the container definition
    this.registerGraphDefinition(modifiedContainerDefinition);

    // Create the new encapsulated instance in all container instances
    // Node ID in the parent is the instance ID (unique), matching the definition graph
    const nodeIdInParent = newInstanceId;
    for (let containerInstance of containerInstances) {
      // Use composite ID for sub-instances to match syncRootDefinition behavior
      const subInstanceId = `${containerInstance.id}/${nodeIdInParent}`;

      // Track containing instance BEFORE instantiate so exposed value propagation works
      this.containedBy[subInstanceId] = containerInstance.id;

      // Create the sub-graph instance from the new encapsulated definition
      const subInstance = this.instantiate(newDefinitionId, subInstanceId);
      containerInstance.nodes[nodeIdInParent] = subInstance;

      // Create exposed endpoints for the new sub-graph on the container instance
      const exposedConfigs = this.buildGraphEndpoints(newDefinition);
      this.createEndpoints(containerInstance, subInstance, exposedConfigs);

      // Connect exposed endpoints to internal endpoints
      this.connectSubgraphEndpoints(containerInstance, nodeIdInParent, subInstance, newDefinition);

      // Emit values for exposed endpoints on the container
      // This makes the subgraph's exposed endpoints visible to the container's UI
      for (const [exposedName, exposed] of Object.entries(newDefinition.api)) {
        const [internalNodeId, internalEndpointId] = (exposed as ExposedEndpoint).path.split('.');
        const value = this.store.getValue({
          instancePath: subInstanceId,
          nodeId: internalNodeId,
          endpointId: internalEndpointId
        });
        if (value !== undefined) {
          this.pushValue(containerInstance.id, nodeIdInParent, exposedName, value);
        }
      }

      // Wire new connections
      this.applyConnections(containerInstance, modifiedContainerDefinition);

      this.logTrace(`[encapsulate] Added subInstance ${subInstanceId} (nodeId: ${nodeIdInParent}) to container ${containerInstance.id}, nodeKeys: ${Object.keys(containerInstance.nodes).join(', ')}`);
    }

    // Verify node exists before emit
    const verifyInstanceIds = this.getInstancesOfGraph(definitionId);
    const verifyInstance = verifyInstanceIds[0] ? this.instances[verifyInstanceIds[0]] : null;
    this.logTrace(`[encapsulate] Before emit - definitionId: ${definitionId}, nodeIdInParent: ${nodeIdInParent}, verifyInstanceId: ${verifyInstanceIds[0]}, nodeExists: ${verifyInstance ? !!verifyInstance.nodes[nodeIdInParent] : false}, nodeKeys: ${verifyInstance ? Object.keys(verifyInstance.nodes).join(',') : 'N/A'}`);

    // push definition with new node IDs for auto-layout
    this.pushDefinition(definitionId, [nodeIdInParent]);

    return newInstanceId;
  }

  // ─────────────────────────────────────────────────────────────
  // Instance Management
  // ─────────────────────────────────────────────────────────────

  /**
   * Create an instance of a graph definition.
   * Sub-graphs become GraphInstance nodes (encapsulated, not flattened).
   * @param initialData Optional data to override defaults (used when loading)
   */
  instantiate(definitionId: string, instanceId: string, initialData?: Record<string, any>): IGraphInstance {
    const graphDefinition = this.graphDefinitions[definitionId];
    if (!graphDefinition) {
      throw new Error(`Graph definition '${definitionId}' not found`);
    }
    // Create instance using GraphInstance class
    // nodeId is the local ID (last part of path), instancePath is the full path
    const nodeId = instanceId.includes('/') ? instanceId.split('/').pop()! : instanceId;
    const instance = new GraphInstance(nodeId, instanceId, graphDefinition, this);

    this.instances[instanceId] = instance;

    // Register instance with store for UI access
    this.store.registerInstance(instanceId, definitionId);

    // Track instance by graph definition
    if (!this.instancesByDefinition[definitionId]) {
      this.instancesByDefinition[definitionId] = new Set();
    }
    this.instancesByDefinition[definitionId].add(instanceId);

    // Create nodes (sub-graphs become GraphInstance nodes, not flattened)
    const _t0 = performance.now();
    this.instantiateNodes(instance, graphDefinition);
    const _t1 = performance.now();

    // Collect config data for direct nodes only
    // Sub-graphs handle their own config via their own instantiate() call
    // Config supports two formats:
    // - Compact format: config[nodeId][endpointId] = value (when only value differs from default)
    // - Full format: config[nodeId][endpointId] = { value, isInput, isOutput, ... } (when metadata also differs)
    // We only extract values here, not metadata
    const nodeInitData: Record<string, Record<string, any>> = {};
    for (const [nodeId, endpoints] of Object.entries(graphDefinition.config)) {
      nodeInitData[nodeId] = {};
      for (const [endpointId, endpointConfig] of Object.entries(endpoints as Record<string, any>)) {
        if (endpointConfig && typeof endpointConfig === 'object' && !Array.isArray(endpointConfig)) {
          // Full format: extract only the value field if present
          if ('value' in endpointConfig) {
            nodeInitData[nodeId][endpointId] = endpointConfig.value;
          }
          // Skip if no value (metadata only like isInput/isOutput)
        } else {
          // Compact format: direct value (including arrays)
          nodeInitData[nodeId][endpointId] = endpointConfig;
        }
      }
    }

    // Store default values BEFORE initializing nodes
    // This ensures params proxy returns values when nodes read during initialize()
    const _t2 = performance.now();
    this.storeInitialValues(instance, graphDefinition, nodeInitData);
    const _t3 = performance.now();

    // Apply expression state from config (after store has values so context binds correctly)
    for (const [nodeId, nodeConfig] of Object.entries(graphDefinition.config)) {
      if (!nodeConfig || typeof nodeConfig !== 'object') continue;
      for (const [epId, epConfig] of Object.entries(nodeConfig as Record<string, any>)) {
        if (!epConfig || typeof epConfig !== 'object') continue;
        const ep = instance.endpoints[`${nodeId}.${epId}`];
        if (!ep) continue;
        if ((epConfig as any).useExp === false) {
          (ep as any)._configGet = null;
          (ep as any).configGetFactory = null;
        } else if ((epConfig as any).useExp && (epConfig as any).exp) {
          const fn = compileGetter((epConfig as any).exp);
          if (fn) {
            (ep as any).configGetFactory = fn;
            (ep as any).bindGetter(this.env.transport.PPQ, this.env.transport.beatsPerBar, getSiblingEndpoints(instance, nodeId, epId), () => this.env.transport.ticks);
            // Propagate factory to internal endpoint (exposed → internal)
            if (ep.internalEndpoint) {
              const stored = this.store.getPayload(ep.storeKey);
              ep.setValue(resolveConfigValue(stored));
            }
          }
        }
      }
    }

    // Apply base endpoint useExp/exp defaults for endpoints not already configured
    for (const [nodeId] of Object.entries(instance.nodes)) {
      const nodeType = getNodeType(graphDefinition.nodes[nodeId]);
      const baseEndpoints = this.pluginRegistry.getEndpoints(nodeType);
      if (!baseEndpoints) continue;
      for (const [epId, baseEp] of Object.entries(baseEndpoints)) {
        // Skip if config already handled this endpoint's useExp
        const configUseExpr = (graphDefinition.config[nodeId]?.[epId] as any)?.useExp;
        if (configUseExpr !== undefined) continue;
        // Base endpoint has useExp default with an expression source
        if (baseEp.useExp && baseEp.exp) {
          const ep = instance.endpoints[`${nodeId}.${epId}`];
          if (!ep) continue;
          const fn = compileGetter(baseEp.exp);
          if (fn) {
            (ep as any).configGetFactory = fn;
            (ep as any).bindGetter(this.env.transport.PPQ, this.env.transport.beatsPerBar, getSiblingEndpoints(instance, nodeId, epId), () => this.env.transport.ticks);
          }
        }
      }
    }
    const _t4 = performance.now();

    // Initialize all nodes and apply values from store
    for (const [, node] of Object.entries(instance.nodes)) {
      try {
        node.initialize();
      } catch (e) {
        logger.error('Runtime', 'Error initializing node')
      }
    }
    const _t5 = performance.now();


    // Apply initial values from store — skip during batch (values will be distributed after batch)
    if (!this.store.isBatching()) {
      for (const [nodeId, node] of Object.entries(instance.nodes)) {
        // Apply initial values from store (skip :in/:out activity keys)
        const nodeValues = this.store.getNodeValues(instanceId, nodeId);
        for (const [endpointId, value] of Object.entries(nodeValues)) {
          if (endpointId.includes(':')) continue;
          try {
            node.onParamChanged(value, endpointId);
          } catch (err) {
            logger.error('Runtime', `Error setting initial value ${instanceId}/${nodeId}.${endpointId}:`, err instanceof Error ? err.message : err);
          }
        }
      }
    }

    // During batch mode, skip UI sync and redundant value pushes — distributeAndCollect handles values
    const batching = this.store.isBatching();

    // Emit initial values for all node endpoints (for UI sync)
    if (!batching) {
      this.pushInitialValues(instance);
    }
    const _t6 = performance.now();

    // Notify nodes of existing connections and wire horizontal connections
    // (connectTo inside already skips initial value push during batch)
    this.notifyExistingConnections(instance, graphDefinition);
    const _t7 = performance.now();

    // Create exposed endpoints for this instance from definition's api
    // Only for root - sub-graphs have their exposed endpoints created by parent
    if (!this.containedBy[instanceId]) {
      const graphConfigs = this.buildGraphEndpoints(graphDefinition);
      for (const config of graphConfigs) {
        // Root's own exposed endpoints: instance acts as both container and node
        const endpoint = this.createEndpoint(instance, instance, config);
        if (endpoint) {
          instance.endpoints[config.id] = endpoint;
        }
      }
      // Connect root's exposed endpoints to internal endpoints
      this.connectExposedEndpoints(instance, graphDefinition);
    }

    // Apply initial values for sub-graphs (root definition has no api, so nothing to apply)
    if (graphDefinition.id !== 'root') {
      instance.initialize();

      // During batch, skip API value application — distributeAndCollect will push real values
      // through the wired connections after all children are created
      if (!batching) {
        // Collect values: api defaults merged with initialData from containing def
        const defaults: Record<string, any> = {};
        for (const [key, ep] of Object.entries(graphDefinition.api)) {
          if (ep.value !== undefined) {
            defaults[key] = ep.value;
          }
        }
        const dataToApply = { ...defaults, ...initialData };

        // Apply parent config overrides to internal nodes via exposed endpoint paths.
        // Must use endpoint.setValue() (not pushValue + onParamChanged) so values
        // propagate through internal connections. Without this, a parent override
        // on an exposed param only reaches the source node — downstream nodes
        // connected to that param inside the sub-graph won't receive the value.
        for (const [exposedName, value] of Object.entries(dataToApply)) {
          const exposedEndpoint = graphDefinition.api[exposedName];
          if (!exposedEndpoint) continue;

          const [internalNodeId, internalEndpointId] = exposedEndpoint.path.split('.');
          const ep = instance.endpoints[`${internalNodeId}.${internalEndpointId}`];
          if (ep) {
            ep.setValue(value);
          }
        }
      }
    }
    const _t8 = performance.now();

    // Emit nodesAdded for all nodes in this instance
    const nodeIds = Object.keys(instance.nodes);
    if (nodeIds.length > 0) {
      this.pushDefinition(definitionId, nodeIds);
    }
    const _t9 = performance.now();

    // Log timing for non-trivial instances (skip leaf nodes)
    const totalMs = _t9 - _t0;
    if (totalMs > 1) {
      logger.info('Runtime', `instantiate ${definitionId} (${instanceId}): nodes=${(_t1-_t0).toFixed(1)}ms store=${(_t3-_t2).toFixed(1)}ms expr=${(_t4-_t3).toFixed(1)}ms init=${(_t5-_t4).toFixed(1)}ms vals+push=${(_t6-_t5).toFixed(1)}ms conns=${(_t7-_t6).toFixed(1)}ms api+init=${(_t8-_t7).toFixed(1)}ms pushDef=${(_t9-_t8).toFixed(1)}ms total=${totalMs.toFixed(1)}ms`);
    }

    return instance;
  }

  /**
   * Instantiate nodes within a graph definition.
   * Sub-graphs become GraphInstance nodes (not flattened).
   */
  private instantiateNodes(
    instance: IGraphInstance,
    graphDefinition: GraphDefinition
  ): void {
    logger.trace('Runtime', `[instantiateNodes] instance=${instance.id}, def=${graphDefinition.id}`);
    for (const [nodeId, nodeEntry] of Object.entries(graphDefinition.nodes)) {
      const nodeType = getNodeType(nodeEntry);
      if (this.pluginRegistry.hasNode(nodeType)) {
        // Create node via plugin
        const context = this.createDeps(instance.instancePath, nodeId, nodeType);
        const node = this.pluginRegistry.createNode(nodeType, nodeId, context);
        logger.trace('Runtime', `[instantiateNodes] created ${instance.id}.${nodeId} (${nodeType})`);
        instance.nodes[nodeId] = node;
        this.graphIndex.addNode(instance.instancePath, nodeId, node);

        // Create DataEndpoint instances for this node's data endpoints
        // Merge base endpoints from plugin + custom params from config
        const baseEndpoints = this.pluginRegistry.getEndpoints(nodeType) ?? {};
        const nodeConfig = graphDefinition.config[nodeId];
        const customParams = getCustomParams(nodeConfig, baseEndpoints);
        const allEndpoints = [...endpointsToArray(baseEndpoints), ...customParams];
        this.createEndpoints(instance, node, allEndpoints);

      } else {
        // Create sub-graph as a GraphInstance node (implements INode)
        const subGraphDefinition = this.graphDefinitions[nodeType];
        if (!subGraphDefinition) {
          throw new Error(`Unknown node type: ${nodeType}`);
        }

        // Track containing instance before instantiate
        // Use composite ID for sub-instances to avoid collision when same definition is used multiple times
        const subInstanceId = `${instance.instancePath}/${nodeId}`;
        this.containedBy[subInstanceId] = instance.instancePath;

        // Collect config values for this sub-graph node to pass as initialData
        // This ensures saved config values (like rate: 16) propagate through to nested nodes
        const nodeConfig = graphDefinition.config[nodeId] ?? {};
        const subInitialData: Record<string, any> = {};
        for (const [endpointId, endpointConfig] of Object.entries(nodeConfig)) {
          subInitialData[endpointId] = resolveConfigValue(endpointConfig);
        }

        const subInstance = this.instantiate(nodeType, subInstanceId, subInitialData);
        instance.nodes[nodeId] = subInstance;

        // Create exposed endpoints for sub-graph on the containing instance
        const exposedConfigs = this.buildGraphEndpoints(subGraphDefinition);
        this.createEndpoints(instance, subInstance, exposedConfigs);

        // Connect exposed endpoints to internal endpoints
        this.connectSubgraphEndpoints(instance, nodeId, subInstance, subGraphDefinition);
      }
    }
  }

  /**
   * Create endpoint instances for a node's endpoints.
   * Endpoints are stored on instance.endpoints with key 'nodeId.endpointId'.
   * Uses the plugin registry for custom endpoint types, falls back to DataEndpoint.
   *
   * @param nodeId - The node's id within the containing instance
   */
  private createEndpoints(
    instance: IGraphInstance,
    node: INode,
    configs: Endpoint[]
  ): void {
    instance.endpoints = instance.endpoints || {};

    for (const config of configs) {
      const endpoint = this.createEndpoint(instance, node, config);
      const key = `${node.id}.${config.id}`;
      instance.endpoints[key] = endpoint;
    }
  }

  /**
   * Create a single endpoint instance using the plugin registry.
   * Falls back to DataEndpoint if no plugin handles this dataType.
   */
  private createEndpoint(
    instance: IGraphInstance,
    node: INode,
    endpoint: Endpoint
  ): IEndpoint {
    // Build dependencies for endpoint factory
    const config: EndpointWithContext = {
      instancePath: instance.instancePath,
      node,
      endpoint,
      graphIndex: this.graphIndex
    };

    return this.pluginRegistry.createEndpoint(config);

  }

  /**
   * Build config for a single exposed endpoint from definition's api.
   */
  private buildEndpoint(definition: GraphDefinition, exposedName: string): Endpoint {
    const exposedEndpoint = definition.api[exposedName];
    if (!exposedEndpoint) {
      throw new Error(`Exposed endpoint '${exposedName}' not found in definition '${definition.id}'`);
    }

    const [internalNodeId, internalEndpointId] = exposedEndpoint.path.split('.');
    const internalEndpoint = this.getEndpoint(definition.id, internalNodeId, internalEndpointId);
    if (!internalEndpoint) {
      throw new Error(`Internal endpoint '${exposedEndpoint.path}' not found for exposed endpoint '${exposedName}' in definition '${definition.id}'`);
    }

    const defaults = getExposedEndpointDefaults(internalEndpoint);

    return {
      id: exposedName,
      dataType: internalEndpoint.dataType,
      dataFlow: internalEndpoint.dataFlow,
      isInput: exposedEndpoint.isInput ?? defaults.isInput,
      isOutput: exposedEndpoint.isOutput ?? defaults.isOutput,
      min: exposedEndpoint.min ?? internalEndpoint.min,
      max: exposedEndpoint.max ?? internalEndpoint.max,
      step: exposedEndpoint.step ?? internalEndpoint.step,
      options: exposedEndpoint.options ?? internalEndpoint.options,
      optionLabels: exposedEndpoint.optionLabels ?? internalEndpoint.optionLabels,
      optionInfo: exposedEndpoint.optionInfo ?? internalEndpoint.optionInfo,
      scaleIncomingValues: exposedEndpoint.scaleIncomingValues ?? internalEndpoint.scaleIncomingValues,
    };
  }

  /**
   * Add an endpoint instance for a node at runtime.
   * Used when exposing an endpoint dynamically.
   * Stores on instance.endpoints with key 'nodeId.endpointId'.
   */
  private addNodeEndpoint_(
    instance: IGraphInstance,
    node: INode,
    endpoint: Endpoint
  ): void {
    instance.endpoints = instance.endpoints || {};
    instance.endpoints[`${node.id}.${endpoint.id}`] = this.createEndpoint(instance, node, endpoint);
  }

  /**
   * Add an endpoint instance for an instance's own exposed endpoint.
   * Used when exposing an endpoint dynamically on a top-level instance.
   * Stores on instance.endpoints with key 'endpointId' (no node prefix).
   */
  private addExposedEndpoint(instance: IGraphInstance, config: Endpoint): void {
    const endpoint = this.createEndpoint(instance, instance, config);
    if (!endpoint) return;
    instance.endpoints = instance.endpoints || {};
    instance.endpoints[config.id] = endpoint;
  }

  /**
   * Remove an endpoint instance at runtime.
   * Used when unexposing an endpoint dynamically.
   */
  private removeEndpointFromInstance(instance: IGraphInstance, key: string): void {
    if (instance.endpoints) {
      delete instance.endpoints[key];
    }
  }

  /**
   * Build endpoint configs for a GraphInstance from its definition's api.
   * Uses stored isInput/isOutput from api (defaults to false if not specified).
   * Does NOT re-derive defaults - that only happens when first exposing.
   */
  private buildGraphEndpoints(definition: GraphDefinition): Endpoint[] {
    const configs: Endpoint[] = [];

    for (const [exposedName, exposedEndpoint] of Object.entries(definition.api)) {
      const [internalNodeId, internalEndpointId] = exposedEndpoint.path.split('.');
      const internalEndpoint = this.getEndpoint(definition.id, internalNodeId, internalEndpointId);
      if (!internalEndpoint) {
        throw new Error(`Internal endpoint '${exposedEndpoint.path}' not found for exposed endpoint '${exposedName}' in definition '${definition.id}'`);
      }

      configs.push({
        id: exposedName,
        dataType: internalEndpoint.dataType,
        dataFlow: internalEndpoint.dataFlow,
        isInput: exposedEndpoint.isInput ?? false,
        isOutput: exposedEndpoint.isOutput ?? false,
        min: exposedEndpoint.min ?? internalEndpoint.min,
        max: exposedEndpoint.max ?? internalEndpoint.max,
        step: exposedEndpoint.step ?? internalEndpoint.step,
        scaleIncomingValues: exposedEndpoint.scaleIncomingValues ?? internalEndpoint.scaleIncomingValues,
      });
    }

    // Add preset endpoint if definition has presets (same as primitives with factoryPresets)
    const hasPresets = (definition.presets && definition.presets.length > 0) ||
      (definition.userPresets && definition.userPresets.length > 0);
    if (hasPresets) {
      const allPresets = [...(definition.presets ?? []), ...(definition.userPresets ?? [])];
      configs.push({
        id: 'preset',
        dataType: 'string',
        isInput: true,
        isOutput: false,
        options: allPresets.map(p => p.name),
      });
    }

    return configs;
  }

  /**
   * Connect exposed endpoints to their internal endpoints.
   * - Exposed inputs: add internal endpoint to exposed endpoint's targetEndpoints
   * - Exposed outputs: add exposed endpoint to internal endpoint's targetEndpoints
   */
  /**
   * Connect root's exposed endpoints to internal endpoints.
   * Root's exposed endpoints are keyed by just the endpoint name.
   */
  private connectExposedEndpoints(instance: IGraphInstance, definition: GraphDefinition): void {
    for (const [exposedName, exposedEndpoint] of Object.entries(definition.api)) {
      const exposed = instance.endpoints[exposedName] as IEndpoint;
      if (!exposed) {
        throw new Error(`Exposed endpoint '${exposedName}' not found in instance '${instance.id}'`);
      }

      const [internalNodeId, internalEndpointId] = exposedEndpoint.path.split('.');

      // Internal endpoint is on this instance with key 'nodeId.endpointId'
      const internal = instance.endpoints[`${internalNodeId}.${internalEndpointId}`];
      if (!internal) {
        throw new Error(`Internal endpoint '${internalNodeId}.${internalEndpointId}' not found for exposed endpoint '${exposedName}'`);
      }

      // Use subgraph mapping for exposed->internal (no :in on internal)
      // Use regular connection for internal->exposed (outputs bubbling up)
      exposed.connectInternal(internal);
      internal.connectTo(exposed);
    }
  }

  /**
   * Connect a sub-graph's exposed endpoints to its internal endpoints.
   * Exposed endpoints are on containing instance as 'nodeId.exposedName'.
   * Internal endpoints are on sub-instance as 'internalNodeId.endpointId'.
   */
  private connectSubgraphEndpoints(
    containingInstance: IGraphInstance,
    nodeId: string,
    subInstance: IGraphInstance,
    definition: GraphDefinition
  ): void {
    for (const [exposedName, exposedEndpoint] of Object.entries(definition.api)) {
      // Exposed endpoint is on containing instance
      const exposed = containingInstance.endpoints[`${nodeId}.${exposedName}`] as IEndpoint;
      if (!exposed) continue;

      const [internalNodeId, internalEndpointId] = exposedEndpoint.path.split('.');

      // Internal endpoint is on sub-instance
      const internal = subInstance.endpoints[`${internalNodeId}.${internalEndpointId}`];
      if (!internal) {
        throw new Error('internal endpoint not found: ' + `${internalNodeId}.${internalEndpointId}`)
        // continue; // May be audio endpoint, skip
      }


      // Use subgraph mapping for exposed->internal (no :in on internal)
      // Use regular connection for internal->exposed (outputs bubbling up)
      exposed.connectInternal(internal);
      internal.connectTo(exposed);

      // Register internal edges for subgraph boundary traversal
      this.graphIndex.addSubgraphEdge({
        parentInstancePath: containingInstance.instancePath,
        subNodeId: nodeId,
        subInstancePath: subInstance.instancePath,
        internalNodeId,
        exposedName,
        internalEndpointId,
        isOutput: exposed.isOutput
      });
    }
  }

  /**
   * Notify nodes of existing connections by calling connection hooks.
   * - onConnectedTo on source node (e.g., envelope can disable target's ADSR)
   * - onConnectedFrom on target node
   * Also tracks wired connections for sync diffing.
   *
   * Note: Sub-graphs are now GraphInstance nodes with virtual endpoints.
   * No recursion needed - each instance handles its own connections.
   */
  private notifyExistingConnections(
    instance: IGraphInstance,
    graphDefinition: GraphDefinition
  ): void {
    // Initialize wired connections set for this instance
    if (!this.wiredConnections[instance.instancePath]) {
      this.wiredConnections[instance.instancePath] = new Set();
    }

    // Process connections in this definition
    for (const conn of graphDefinition.connections) {
      const [sourceNodeId, sourceEp] = conn.source.split('.');
      const [targetNodeId, targetEp] = conn.target.split('.');

      const sourceNode = instance.nodes[sourceNodeId];
      const targetNode = instance.nodes[targetNodeId];

      if (!sourceNode || !targetNode) {
        logger.error('Runtime', `Connection ${conn.source} -> ${conn.target}: node not found`);
        continue;
      }

      // Connect endpoint instances (connectTo handles initial value propagation)
      const srcEp = instance.endpoints[`${sourceNodeId}.${sourceEp}`];
      const tgtEp = instance.endpoints[`${targetNodeId}.${targetEp}`];

      // Register edge BEFORE connectTo — connectTo triggers synchronous data flow
      // which may cause downstream nodes to query the graph index
      this.graphIndex.addEdge({
        source: 'horizontal',
        sourceKey: this.graphIndex.nodeKey(instance.instancePath, sourceNodeId),
        targetKey: this.graphIndex.nodeKey(instance.instancePath, targetNodeId),
        sourceEndpointId: sourceEp,
        targetEndpointId: targetEp,
        weight: 1
      });

      // Notify source node it connected to target
      if (sourceNode.onConnectedTo && tgtEp) {
        sourceNode.onConnectedTo(sourceEp, targetNode, tgtEp);
      }

      // Notify target node something connected to it (with source for multi-input tracking)
      if (targetNode.onConnectedFrom && srcEp) {
        targetNode.onConnectedFrom(targetEp, srcEp);
      }
      if (srcEp && tgtEp) {
        srcEp.connectTo(tgtEp);
        tgtEp.onConnectedFrom(srcEp);
      }

      // Track this connection as wired
      this.wiredConnections[instance.instancePath].add(`${conn.source}->${conn.target}`);
    }
  }

  getInstance(instanceId: string): IGraphInstance | undefined {
    return this.instances[instanceId];
  }

  /**
   * Get an endpoint's value directly from the endpoint object.
   * For data endpoints, returns the stored value.
   * For audio endpoints, returns the current level from the analyser.
   * @param endpointKey - Pre-built key in format "nodeId.endpointId"
   */

  //NB THIS IS ONLY USED TO GET THE LEVEL ON AUDIO ENDPOINTS! ALL OTHER VALUES READ FROM STORE
  getEndpointValue(instanceId: string, endpointKey: string): any {
    const instance = this.instances[instanceId];
    if (!instance) {
      throw new Error(`Instance '${instanceId}' not found`);
    }
    const endpoint = instance.endpoints[endpointKey];
    if (!endpoint) {
      throw new Error(`Endpoint '${endpointKey}' not found in instance '${instanceId}'`);
    }
    return endpoint.getValue();
  }

  /**
   * Get the instance ID of the instance that contains this one as a node.
   */
  getContainingInstanceId(instanceId: string): string | undefined {
    return this.containedBy[instanceId];
  }

  /**
   * Set the log level for a specific node in an instance
   */
  setNodeLogLevel(instanceId: string, nodeId: string, level: string): void {
    const instance = this.instances[instanceId];
    if (!instance) {
      throw new Error(`setNodeLogLevel: instance '${instanceId}' not found`);
    }
    const node = instance.nodes[nodeId];
    if (!node) {
      throw new Error(`setNodeLogLevel: node '${nodeId}' not found in instance '${instanceId}'`);
    }
    // Set log level on the node (BaseNode has logLevel property)
    (node as any).logLevel = level;

    const definitionId = instance.definitionId;
    this.pushDefinition(definitionId)
  }

  /**
   * Set a custom label for a node in a definition.
   * Labels are stored in the node config and persist with the project.
   */
  setNodeLabel(definitionId: string, nodeId: string, label: string | undefined): void {
    const definition = this.graphDefinitions[definitionId];
    if (!definition) {
      throw new Error(`Definition '${definitionId}' not found`);
    }

    const nodeEntry = definition.nodes[nodeId];
    if (!nodeEntry) {
      throw new Error(`Node '${nodeId}' not found in definition '${definitionId}'`);
    }

    // Convert to NodeConfig if it's a string, then set label
    if (typeof nodeEntry === 'string') {
      definition.nodes[nodeId] = { type: nodeEntry, label };
    } else {
      if (label) {
        nodeEntry.label = label;
      } else {
        delete nodeEntry.label;
      }
    }

    this.pushDefinition(definitionId);
  }

  /**
   * Get the label for a node in a definition.
   */
  getNodeLabel(definitionId: string, nodeId: string): string | undefined {
    const definition = this.graphDefinitions[definitionId];
    if (!definition) return undefined;

    const nodeEntry = definition.nodes[nodeId];
    if (!nodeEntry) return undefined;

    return typeof nodeEntry === 'string' ? undefined : nodeEntry.label;
  }

  /**
   * Set the visualization node for a definition.
   * This node's renderer will be used for visualization in the parent graph.
   */
  setVizNode(definitionId: string, nodeId: string | null): void {
    const definition = this.graphDefinitions[definitionId];
    if (!definition) {
      throw new Error(`Definition '${definitionId}' not found`);
    }

    if (nodeId) {
      definition.vizNode = nodeId;
    } else {
      delete definition.vizNode;
    }

    this.pushDefinition(definitionId);
  }


  /**
   * Add nodes to an existing instance (e.g., adding to root graph)
   * Updates both the instance and its graph definition
   *
   * For sub-graphs (non-primitives):
   * - The sub-graph stays as a reference in the definition (not flattened)
   * - At runtime, nodes are created with prefixed IDs (e.g., seq1.clock, seq1.player)
   * - Data flow through sub-graph internal connections is handled by propagateInternal
   *
   * Returns the created node IDs (top-level, not flattened)
   */

  /**
   * Add nodes to a definition and all its instances (for live editing)
   */
  addNodesToDefinition(
    definitionId: string,
    nodes: Record<string, string | { type: string; label?: string }>,
    connections: { source: string; target: string }[],
    defaults: Record<string, any> = {}
  ): string[] {
    const graphDefinition = this.graphDefinitions[definitionId];
    if (!graphDefinition) {
      throw new Error(`Graph definition '${definitionId}' not found`);
    }

    // Suppress emissions during node addition
    this.loading = true;
    const createdNodeIds: string[] = Object.keys(nodes);


    // Update definition first
    for (const [nodeId, nodeEntry] of Object.entries(nodes)) {
      // Accept either a string (type) or a NodeConfig object with optional label
      graphDefinition.nodes[nodeId] = typeof nodeEntry === 'string'
        ? nodeEntry
        : nodeEntry;
    }

    for (const conn of connections) {
      graphDefinition.connections.push(conn);
    }

    for (const [path, value] of Object.entries(defaults)) {
      const [defNodeId, ...rest] = path.split('.');
      const defEndpoint = rest.join('.');
      if (!graphDefinition.config[defNodeId]) {
        graphDefinition.config[defNodeId] = {};
      }
      graphDefinition.config[defNodeId][defEndpoint] = value;
    }

    // Create nodes in all instances of this definition
    const instanceIds = this.getInstancesOfGraph(definitionId);
    for (const instanceId of instanceIds) {
      const instance = this.instances[instanceId];


      for (const [nodeId, nodeEntry] of Object.entries(nodes)) {
        const nodeType = getNodeType(nodeEntry);
        if (this.pluginRegistry.hasNode(nodeType)) {
          try {

            const context = this.createDeps(instanceId, nodeId, nodeType);
            const node = this.pluginRegistry.createNode(nodeType, nodeId, context);
            instance.nodes[nodeId] = node;
            this.graphIndex.addNode(instanceId, nodeId, node);

            // Create DataEndpoint instances for this node's data endpoints
            // Merge base endpoints from plugin + custom params from config
            const baseEndpoints = this.pluginRegistry.getEndpoints(nodeType) ?? {};
            const nodeConfig = graphDefinition.config[nodeId] ?? {};
            const customParams = getCustomParams(nodeConfig, baseEndpoints);
            const allEndpoints = [...endpointsToArray(baseEndpoints), ...customParams];
            this.createEndpoints(instance, node, allEndpoints);

            // Store defaults BEFORE initialize so params proxy works
            // Priority: definition config > endpoint default
            // Handle base endpoints
            for (const [epId, ep] of Object.entries(baseEndpoints)) {
              const config = nodeConfig[epId];
              const value = config !== undefined ? resolveConfigValue(config) : ep.value;
              if (value !== undefined) {
                this.pushValue(instanceId, nodeId, epId, value);
              }
            }
            // Handle custom params
            for (const ep of customParams) {
              if (ep.id && ep.value !== undefined) {
                this.pushValue(instanceId, nodeId, ep.id, ep.value);
              }
            }


            node.initialize();
            // Apply initial values from store (skip :in/:out activity keys)
            const nodeValues = this.store.getNodeValues(instanceId, nodeId);
            for (const [endpointId, value] of Object.entries(nodeValues)) {
              if (endpointId.includes(':')) continue;
              node.onParamChanged(value, endpointId);
            }



            // Emit initial values for this node (for :out keys and UI sync)
            this.pushInitialNodeValues(instanceId, nodeId, node);


          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            logger.error('Runtime: Error adding node', msg, nodeType, err instanceof Error ? err.stack : '');
            throw new Error(msg)
          }

        } else {
          const subGraphDef = this.graphDefinitions[nodeType];
          if (!subGraphDef) {
            throw new Error(`Unknown node type: ${nodeType}`);
          }

          // Store exposed endpoint values in parent store
          const nodeConfig = graphDefinition.config[nodeId] ?? {};
          this.store.ensureNodeEntry(instanceId, nodeId);
          for (const [endpointId, exposedEp] of Object.entries(subGraphDef.api)) {
            const config = nodeConfig[endpointId];
            const value = config !== undefined ? resolveConfigValue(config) : exposedEp.value;
            if (value !== undefined) {
              this.pushValue(instanceId, nodeId, endpointId, value);
            }
          }

          // Use composite ID for sub-instances to avoid collision when same definition is used multiple times
          const subInstanceId = `${instanceId}/${nodeId}`;
          this.containedBy[subInstanceId] = instanceId;
          // Pass config so sub-graph receives parent's config overrides
          const subInstance = this.instantiate(nodeType, subInstanceId, nodeConfig);
          instance.nodes[nodeId] = subInstance;

          // Create exposed endpoints for sub-graph on the containing instance
          const exposedConfigs = this.buildGraphEndpoints(subGraphDef);
          this.createEndpoints(instance, subInstance, exposedConfigs);

          // Connect exposed endpoints to internal endpoints
          this.connectSubgraphEndpoints(instance, nodeId, subInstance, subGraphDef);
        }
      }

      // Wire up connections for this instance
      this.applyConnections(instance, graphDefinition);

      // Apply defaults to this instance using the endpoint system (to propagate through connections)
      for (const [path, value] of Object.entries(defaults)) {
        const resolvedPath = this.resolvePathForInstance(graphDefinition, path);
        const parts = resolvedPath.split('.');
        const nodeId = parts.slice(0, -1).join('.');
        const endpointId = parts[parts.length - 1];
        // Use endpoint.setValue to store, notify, and propagate
        const epKey = `${nodeId}.${endpointId}`;
        const ep = instance.endpoints[epKey];
        if (ep) {
          ep.setValue(value);
        }
      }
    }

    // Re-enable emissions
    this.loading = false;

    // Collect all definitions that need to be pushed to the store (including nested sub-graphs)
    const defsToEmit: Record<string, GraphDefinition> = {};
    for (const nodeEntry of Object.values(nodes)) {
      const nodeType = getNodeType(nodeEntry);
      if (!this.pluginRegistry.hasNode(nodeType)) {
        const subDef = this.graphDefinitions[nodeType];
        if (subDef) defsToEmit[nodeType] = subDef;
      }
    }
    defsToEmit[definitionId] = graphDefinition;

    // Emit in dependency order (topological sort)
    const pushOrder = this.getDefinitionEmitOrder(defsToEmit);
    for (const defId of pushOrder) {
      if (defId === definitionId) {
        this.pushDefinition(defId, createdNodeIds);
      } else {
        this.pushDefinition(defId);
      }
    }

    return createdNodeIds;
  }

  /**
   * Remove a node from a definition and all its instances
   */
  removeNodeFromDefinition(definitionId: string, nodeId: string): void {

    const def = this.graphDefinitions[definitionId];
    if (!def) {
      throw new Error(`Definition not found`);
    }

    // Clean up exposed API entries referencing the deleted node
    const exposedToRemove: string[] = [];
    for (const [, exposed] of Object.entries(def.api)) {
      const [internalNodeId] = exposed.path.split('.');
      if (internalNodeId === nodeId) {
        exposedToRemove.push(exposed.path);
      }
    }
    for (const path of exposedToRemove) {
      this.unexposeEndpoint(definitionId, path);
    }

    // Clean up vizNode reference
    if (def.vizNode === nodeId) {
      delete def.vizNode;
    }

    // Find connections involving this node
    const connectionsToRemove = def.connections.filter(conn => {
      const [srcNodeId] = conn.source.split('.');
      const [tgtNodeId] = conn.target.split('.');
      return srcNodeId === nodeId || tgtNodeId === nodeId;
    });

    // Deactivate connections on all instances (triggers onDisconnectedFrom hooks)
    const instanceIds = this.getInstancesOfGraph(definitionId);
    for (const conn of connectionsToRemove) {
      for (const instanceId of instanceIds) {
        const instance = this.instances[instanceId];
        if (instance) {
          try {
            this.deactivateConnection(instance, def, conn.source, conn.target);
          } catch (e) {
            // Connection may not be wired yet, ignore
          }
        }
      }
    }

    // Remove connections from definition
    def.connections = def.connections.filter(conn => {
      const [srcNodeId] = conn.source.split('.');
      const [tgtNodeId] = conn.target.split('.');
      return srcNodeId !== nodeId && tgtNodeId !== nodeId;
    });

    // Remove from definition
    delete def.nodes[nodeId];

    // Remove from config
    delete def.config[nodeId];

    // Remove from all instances
    for (const instanceId of instanceIds) {
      const instance = this.instances[instanceId];
      if (instance?.nodes[nodeId]) {
        // Dispose the node if it has a dispose method
        const node = instance.nodes[nodeId] as any;
        if (typeof node.dispose === 'function') {
          try {
            node.dispose();
          } catch (e) {
            // Ignore disposal errors
          }
        }
        delete instance.nodes[nodeId];
      }
    }

    // Emit once for the definition (after all instances updated)
    this.pushDefinition(definitionId);
  }

  /**
   * Resolve a path for setting values on an instance
   * e.g., "seq1.pattern" -> "seq1.player.pattern" (using sub-graph's exposed endpoints)
   */
  private resolvePathForInstance(graphDefinition: GraphDefinition, path: string): string {
    const parts = path.split('.');
    if (parts.length < 2) return path;

    const [nodeId, endpointOrSubNode] = parts;
    const nodeEntry = graphDefinition.nodes[nodeId];
    const nodeType = nodeEntry ? getNodeType(nodeEntry) : undefined;

    // If it's a sub-graph, resolve through exposed endpoints
    if (nodeType && !this.pluginRegistry.hasNode(nodeType)) {
      const subGraphDef = this.graphDefinitions[nodeType];
      const exposedEndpoint = subGraphDef?.api[endpointOrSubNode];
      if (exposedEndpoint) {
        // This is an exposed endpoint name, resolve it
        return `${nodeId}.${exposedEndpoint.path}`;
      }
    }

    // Not a sub-graph or not an exposed endpoint, return as-is
    return path;
  }

  getAllInstances(): IGraphInstance[] {
    return Array.from(Object.values(this.instances));
  }

  disposeInstance(instanceId: string): void {
    const instance = this.instances[instanceId];
    if (!instance) {
      throw new Error(`Instance '${instanceId}' not found in disposeInstance`);
    }

    // Clean up sub-instances from tracking first.
    // Sub-instances have paths like `instanceId/nodeId` (and deeper nesting).
    // GraphInstance.dispose() clears their nodes/endpoints, but they must also
    // be removed from this.instances and instancesByDefinition to avoid orphans.
    const prefix = instanceId + '/';
    for (const subId of Object.keys(this.instances)) {
      if (subId.startsWith(prefix)) {
        const subInstance = this.instances[subId];
        this.graphIndex.removeInstance(subId);
        this.instancesByDefinition[subInstance.definitionId]?.delete(subId);
        delete this.instances[subId];
        delete this.wiredConnections[subId];
        delete this.containedBy[subId];
      }
    }

    // Remove all nodes/edges for this instance from graph index
    this.graphIndex.removeInstance(instanceId);

    // Dispose all nodes
    for (const node of Object.values(instance.nodes)) {
      node.dispose();
    }

    // Remove from tracking
    this.instancesByDefinition[instance.definitionId]?.delete(instanceId);
    delete this.instances[instanceId];
    delete this.wiredConnections[instanceId];
    delete this.containedBy[instanceId];
  }

  // ─────────────────────────────────────────────────────────────
  // Data Flow
  // ─────────────────────────────────────────────────────────────

  /**
   * Set a value on a node's endpoint (public API).
   * Routes to applyTweak for config persistence and proper data flow.
   * @param instanceId - The instance containing the node
   * @param endpointPath - Path in format "nodeId.endpointId" (e.g., "synth1.volume", "seq.pattern")
   * @param value - The value to set
   */
  setValue(instanceId: string, endpointPath: string, value: any): void {
    if (!endpointPath.includes('.')) {
      throw new Error('Endpoint path should be in format "nodeId.endpointId"');
    }

    const { nodeId, endpointId } = fromEndpointPath(endpointPath);

    // Unwrap payload if wrapped
    const rawValue = resolveConfigValue(value);

    this.applyTweak(instanceId, nodeId, endpointId, rawValue);
  }

  /**
   * Get a value from a node's endpoint within an instance.
   * Reads from store (single source of truth).
   *
   * Two forms:
   * - getValue(instanceId, nodeId, endpointId): for internal use
   * - getValue(instanceId, endpointPath): convenience form with path parsing
   */
  getValue(instanceId: string, nodeIdOrPath: string, endpointId?: string): any {
    let nodeId: string;
    let epId: string;

    if (endpointId !== undefined) {
      // 3-arg form: (instanceId, nodeId, endpointId)
      nodeId = nodeIdOrPath;
      epId = endpointId;
    } else {
      // 2-arg form: (instanceId, endpointPath)
      const endpointPath = nodeIdOrPath;
      if (!endpointPath.includes('.')) {
        throw new Error('Endpoint path should be in format "nodeId.endpointId" (e.g., "synth1.volume", "seq.pattern") ')
      }

      // Parse path to get nodeId and endpoint
      const parts = endpointPath.split('.');
      epId = parts.pop()!;
      nodeId = parts.join('.');
    }

    // Read from store (single source of truth)
    const value = this.store.getValue({ instancePath: instanceId, nodeId, endpointId: epId });
    // Unwrap payload if wrapped
    return resolveConfigValue(value);
  }

  // ─────────────────────────────────────────────────────────────
  // Runtime Connections
  // ─────────────────────────────────────────────────────────────

  /**
   * Create a runtime connection between two graph instances
   */
  connect(
    sourceInstanceId: string,
    sourceEndpoint: string,
    targetInstanceId: string,
    targetEndpoint: string
  ): InstanceConnection {
    const sourceInstance = this.instances[sourceInstanceId];
    const targetInstance = this.instances[targetInstanceId];

    this.logInfo(`creating connection ${sourceInstanceId}.${sourceEndpoint} -> ${targetInstanceId}.${targetEndpoint}`)

    if (!sourceInstance) {
      throw new Error(`Source instance '${sourceInstanceId}' not found`);
    }
    if (!targetInstance) {
      throw new Error(`Target instance '${targetInstanceId}' not found`);
    }

    const connection: InstanceConnection = {
      sourceEndpoint,
      targetInstanceId,
      targetEndpoint
    };

    // Add to source instance's connections
    sourceInstance.connections.push(connection);

    // Connect endpoints for automatic propagation
    const { nodeId: srcNodeId, endpointId: srcEndpointId } = fromEndpointPath(sourceEndpoint);
    const { nodeId: tgtNodeId, endpointId: tgtEndpointId } = fromEndpointPath(targetEndpoint);
    const srcEp = sourceInstance.endpoints[`${srcNodeId}.${srcEndpointId}`];
    if (!srcEp) {
      throw new Error(`Source endpoint '${srcNodeId}.${srcEndpointId}' not found in instance '${sourceInstanceId}'`);
    }
    const tgtEp = targetInstance.endpoints[`${tgtNodeId}.${tgtEndpointId}`];
    if (!tgtEp) {
      throw new Error(`Target endpoint '${tgtNodeId}.${tgtEndpointId}' not found in instance '${targetInstanceId}'`);
    }
    // Register edge BEFORE connectTo — connectTo triggers synchronous data flow
    this.graphIndex.addEdge({
      source: 'horizontal',
      sourceKey: this.graphIndex.nodeKey(sourceInstanceId, srcNodeId),
      targetKey: this.graphIndex.nodeKey(targetInstanceId, tgtNodeId),
      sourceEndpointId: srcEndpointId,
      targetEndpointId: tgtEndpointId,
      weight: 1
    });

    srcEp.connectTo(tgtEp);
    tgtEp.onConnectedFrom(srcEp);

    return connection;
  }

  /**
   * Remove a runtime connection
   */
  disconnect(
    sourceInstanceId: string,
    sourceEndpoint: string,
    targetInstanceId: string
  ): void {
    const sourceInstance = this.instances[sourceInstanceId];
    if (!sourceInstance) {
      throw new Error(`Source instance '${sourceInstanceId}' not found`);
    }
    const targetInstance = this.instances[targetInstanceId];
    if (!targetInstance) {
      throw new Error(`Target instance '${targetInstanceId}' not found`);
    }

    // Disconnect endpoints before removing connection
    const { nodeId: srcNodeId, endpointId: srcEndpointId } = fromEndpointPath(sourceEndpoint);
    const srcEp = sourceInstance.endpoints[`${srcNodeId}.${srcEndpointId}`];
    if (!srcEp) {
      throw new Error(`Source endpoint '${srcNodeId}.${srcEndpointId}' not found in instance '${sourceInstanceId}'`);
    }

    for (const conn of sourceInstance.connections) {
      if (conn.sourceEndpoint === sourceEndpoint && conn.targetInstanceId === targetInstanceId) {
        const { nodeId: tgtNodeId, endpointId: tgtEndpointId } = fromEndpointPath(conn.targetEndpoint);
        const tgtEp = targetInstance.endpoints[`${tgtNodeId}.${tgtEndpointId}`];
        if (!tgtEp) {
          throw new Error(`Target endpoint '${tgtNodeId}.${tgtEndpointId}' not found in instance '${targetInstanceId}'`);
        }
        srcEp.disconnectFrom(tgtEp);

        // Remove cross-instance edge from graph index
        this.graphIndex.removeEdge(
          this.graphIndex.nodeKey(sourceInstanceId, srcNodeId),
          this.graphIndex.nodeKey(targetInstanceId, tgtNodeId),
          srcEndpointId,
          tgtEndpointId
        );
      }
    }

    sourceInstance.connections = sourceInstance.connections.filter(
      c => !(c.sourceEndpoint === sourceEndpoint && c.targetInstanceId === targetInstanceId)
    );
  }

  // ─────────────────────────────────────────────────────────────
  // Internal Graph Connections (within a definition)
  // ─────────────────────────────────────────────────────────────

  /**
   * Resolve an endpoint path to the actual node and endpoint.
   * Handles sub-graph exposed endpoints (e.g., "subgraph.exposedEp" -> "subgraph.internalNode.actualEp")
   */
  private resolveNodeEndpoint(
    graphDef: GraphDefinition,
    nodeId: string,
    endpointId: string
  ): { resolvedNodeId: string; resolvedEndpointId: string } {
    const nodeEntry = graphDef.nodes[nodeId];
    if (!nodeEntry) {
      return { resolvedNodeId: nodeId, resolvedEndpointId: endpointId };
    }
    const nodeType = getNodeType(nodeEntry);
    const subGraph = this.graphDefinitions[nodeType];
    const exposedEndpoint = subGraph?.api[endpointId];
    if (exposedEndpoint) {
      const [subNodeId, subEp] = exposedEndpoint.path.split('.');
      return {
        resolvedNodeId: `${nodeId}.${subNodeId}`,
        resolvedEndpointId: subEp
      };
    }
    return { resolvedNodeId: nodeId, resolvedEndpointId: endpointId };
  }

  /**
   * Activate a connection: call lifecycle hooks and connect endpoints.
   * Does not modify the definition - just makes an existing connection live.
   * Used by both addInternalConnection and syncConnections.
   *
   * For hooks (onConnectedTo/onConnectedFrom), we resolve through sub-graph APIs
   * to notify the actual internal nodes. For value propagation, we use the
   * top-level node (which may be a GraphInstance that handles its own routing).
   */
  private activateConnection(
    instance: IGraphInstance,
    graphDef: GraphDefinition,
    source: string,
    target: string
  ): void {
    const [sourceNodeId, sourceEp] = source.split('.');
    const [targetNodeId, targetEp] = target.split('.');

    // Get top-level nodes (may be GraphInstances)
    const sourceNode = instance.nodes[sourceNodeId];
    const targetNode = instance.nodes[targetNodeId];

    if (!sourceNode) {
      throw new Error(`[activateConnection] Source node '${sourceNodeId}' not found in instance '${instance.id}'. Available nodes: ${Object.keys(instance.nodes).join(', ')}`);
    }
    if (!targetNode) {
      throw new Error(`[activateConnection] Target node '${targetNodeId}' not found in instance '${instance.id}'. Available nodes: ${Object.keys(instance.nodes).join(', ')}`);
    }

    // For hooks, resolve through sub-graph APIs to notify internal nodes
    const { resolvedNodeId: resolvedSourceNodeId, resolvedEndpointId: resolvedSourceEp } =
      this.resolveNodeEndpoint(graphDef, sourceNodeId, sourceEp);
    const { resolvedNodeId: resolvedTargetNodeId, resolvedEndpointId: resolvedTargetEp } =
      this.resolveNodeEndpoint(graphDef, targetNodeId, targetEp);

    // If resolved node not found in instance.nodes, fall back to top-level node
    // and use the original endpoint ID (not the resolved internal one)
    const resolvedSourceNode = instance.nodes[resolvedSourceNodeId];
    const resolvedTargetNode = instance.nodes[resolvedTargetNodeId];
    const finalSourceNode = resolvedSourceNode ?? sourceNode;
    const finalSourceEp = resolvedSourceNode ? resolvedSourceEp : sourceEp;
    const finalTargetNode = resolvedTargetNode ?? targetNode;
    const finalTargetEp = resolvedTargetNode ? resolvedTargetEp : targetEp;

    // Connect endpoints for automatic data propagation
    const srcEp = instance.endpoints[`${sourceNodeId}.${sourceEp}`];
    const tgtEp = instance.endpoints[`${targetNodeId}.${targetEp}`];

    // Register edge BEFORE connectTo — connectTo triggers synchronous data flow
    // which may cause downstream nodes to query the graph index
    this.graphIndex.addEdge({
      source: 'horizontal',
      sourceKey: this.graphIndex.nodeKey(instance.instancePath, sourceNodeId),
      targetKey: this.graphIndex.nodeKey(instance.instancePath, targetNodeId),
      sourceEndpointId: sourceEp,
      targetEndpointId: targetEp,
      weight: 1
    });

    // Notify source node it connected to target (handles audio routing)
    if (tgtEp) {
      finalSourceNode.onConnectedTo?.(finalSourceEp, finalTargetNode, tgtEp);
    }
    // Notify target node of connection (with source endpoint for multi-input tracking)
    if (srcEp) {
      finalTargetNode.onConnectedFrom?.(finalTargetEp, srcEp);
    }
    if (srcEp && tgtEp) {
      srcEp.connectTo(tgtEp);
      tgtEp.onConnectedFrom(srcEp);
    }

    // Track that this connection has been wired
    const connKey = `${source}->${target}`;
    if (!this.wiredConnections[instance.instancePath]) {
      this.wiredConnections[instance.instancePath] = new Set();
    }
    this.wiredConnections[instance.instancePath].add(connKey);

    this.logTrace(`[activateConnection] Activated ${source} -> ${target}`);
  }

  /**
   * Deactivate a connection: call disconnection hooks and disconnect endpoints.
   * Does not modify the definition - just tears down an active connection.
   */
  private deactivateConnection(
    instance: IGraphInstance,
    graphDef: GraphDefinition,
    source: string,
    target: string
  ): void {
    // Resolve endpoints
    const [sourceNodeId, sourceEp] = source.split('.');
    const [targetNodeId, targetEp] = target.split('.');
    const { resolvedNodeId: resolvedSourceNodeId, resolvedEndpointId: resolvedSourceEp } =
      this.resolveNodeEndpoint(graphDef, sourceNodeId, sourceEp);
    const { resolvedNodeId: resolvedTargetNodeId, resolvedEndpointId: resolvedTargetEp } =
      this.resolveNodeEndpoint(graphDef, targetNodeId, targetEp);

    // Get top-level nodes for fallback
    const topSourceNode = instance.nodes[sourceNodeId];
    const topTargetNode = instance.nodes[targetNodeId];

    // If resolved node not found in instance.nodes, fall back to top-level node
    // and use the original endpoint ID (not the resolved internal one)
    const resolvedSourceNode = instance.nodes[resolvedSourceNodeId];
    const resolvedTargetNode = instance.nodes[resolvedTargetNodeId];
    const finalSourceNode = resolvedSourceNode ?? topSourceNode;
    const finalSourceEp = resolvedSourceNode ? resolvedSourceEp : sourceEp;
    const finalTargetNode = resolvedTargetNode ?? topTargetNode;
    const finalTargetEp = resolvedTargetNode ? resolvedTargetEp : targetEp;

    // Notify source of disconnection (for audio routing)
    if (finalSourceNode && finalTargetNode) {
      finalSourceNode.onDisconnectedTo?.(finalSourceEp, finalTargetNode, finalTargetEp);
    }

    // Disconnect endpoints
    const srcEp = instance.endpoints[`${sourceNodeId}.${sourceEp}`];
    const tgtEp = instance.endpoints[`${targetNodeId}.${targetEp}`];

    // Notify target of disconnection (with source endpoint for multi-input tracking)
    if (finalTargetNode && srcEp) {
      finalTargetNode.onDisconnectedFrom?.(finalTargetEp, srcEp);
    }

    if (srcEp && tgtEp) {
      srcEp.disconnectFrom(tgtEp);
    }

    // Remove from tracking
    const connKey = `${source}->${target}`;
    this.wiredConnections[instance.instancePath]?.delete(connKey);

    // Remove connection edge from graph index
    this.graphIndex.removeEdge(
      this.graphIndex.nodeKey(instance.instancePath, sourceNodeId),
      this.graphIndex.nodeKey(instance.instancePath, targetNodeId),
      sourceEp,
      targetEp
    );

    this.logTrace(`[deactivateConnection] Deactivated ${source} -> ${target}`);
  }

  /**
   * Add an internal connection within a graph definition.
   * Updates the definition and notifies nodes via hooks.
   */
  addInternalConnection(
    definitionId: string,
    source: string,
    target: string
  ): { added: boolean; message?: string } {
    const graphDef = this.graphDefinitions[definitionId];
    if (!graphDef) {
      throw new Error(`Definition '${definitionId}' not found`);
    }

    this.logInfo(`[addInternalConnection] ${definitionId}: ${source} -> ${target}`)

    // Check if connection already exists
    const exists = graphDef.connections.some(c => c.source === source && c.target === target);
    if (exists) {
      return { added: false, message: 'Connection already exists' };
    }

    // Add connection to definition
    graphDef.connections.push({ source, target });

    // Wire the connection on all instances of this definition
    // Use try-finally to ensure definition is always pushed
    const instanceIds = this.instancesByDefinition[definitionId] || [];
    let wireError: Error | null = null;

    try {
      for (const instanceId of instanceIds) {
        const instance = this.instances[instanceId];
        this.activateConnection(instance, graphDef, source, target);
      }
    } catch (err) {
      wireError = err instanceof Error ? err : new Error(String(err));
      logger.error('Runtime', `[addInternalConnection] Wire failed: ${wireError.message}`);
    }

    // Always push definition change so UI updates
    this.pushDefinition(definitionId);
    this.logInfo(`[addInternalConnection] Definition pushed for ${definitionId}, connections count: ${graphDef.connections.length}`);

    // Re-throw if wiring failed (after pushing definition)
    if (wireError) {
      throw wireError;
    }

    return { added: true };
  }

  /**
   * Remove an internal connection within a graph definition.
   * Updates the definition and notifies nodes via hooks.
   */
  removeInternalConnection(
    definitionId: string,
    source: string,
    target: string
  ): { removed: boolean; message?: string } {
    const graphDef = this.graphDefinitions[definitionId];
    if (!graphDef) {
      throw new Error(`Definition '${definitionId}' not found`);
    }

    // Remove connection from definition
    const before = graphDef.connections.length;
    graphDef.connections = graphDef.connections.filter(
      c => !(c.source === source && c.target === target)
    );

    if (graphDef.connections.length === before) {
      return { removed: false, message: 'Connection not found' };
    }

    this.logInfo(`[removeConnection] ${source} -> ${target}`);

    // Unwire the connection on all instances of this definition

    for (const instanceId of this.instancesByDefinition[definitionId] || []) {
      const instance = this.instances[instanceId];
      this.deactivateConnection(instance, graphDef, source, target);
    }


    // Emit callback for definition change
    this.pushDefinition(definitionId);

    return { removed: true };
  }

  /**
   * Set mute state on a connection.
   * Delegates to the source endpoint's setConnectionDisabled method,
   * which handles it appropriately for the endpoint type (data vs audio).
   * Edge ID format: "nodeId.endpoint->nodeId.endpoint"
   */
  setConnectionMuted(instanceId: string, edgeId: string, muted: boolean): void {
    const instance = this.instances[instanceId];
    if (!instance) return;

    const [source, target] = edgeId.split('->');
    if (!source || !target) return;

    const [sourceNodeId, sourceEndpoint] = source.split('.');
    const [targetNodeId, targetEndpoint] = target.split('.');

    const sourceEp = instance.endpoints[`${sourceNodeId}.${sourceEndpoint}`];
    const targetEp = instance.endpoints[`${targetNodeId}.${targetEndpoint}`];

    if (!sourceEp || !targetEp) return;

    sourceEp.setConnectionDisabled(targetEp, muted);

    // Notify target node so it can tear down/rebuild auxiliary chains
    // (e.g., AudioOutputNode's per-source spatial panning chains)
    const targetNode = instance.nodes[targetNodeId];
    if (targetNode) {
      if (muted) {
        targetNode.onDisconnectedFrom(targetEndpoint, sourceEp);
      } else {
        targetNode.onConnectedFrom(targetEndpoint, sourceEp);
      }
    }
  }

  /**
   * Check if a connection is muted (disabled)
   */
  isConnectionMuted(definitionId: string, edgeId: string): boolean {
    const def = this.graphDefinitions[definitionId];
    if (!def) return false;

    const [source, target] = edgeId.split('->');
    const conn = def.connections.find(c => c.source === source && c.target === target);
    return conn?.disabled ?? false;
  }

  /**
   * Set disabled state on a connection
   * Updates definition, handles audio muting, pushes updated definition to store
   */
  setConnectionDisabled(definitionId: string, edgeId: string, disabled: boolean): void {
    const def = this.graphDefinitions[definitionId];
    if (!def) return;

    const [source, target] = edgeId.split('->');
    const conn = def.connections.find(c => c.source === source && c.target === target);
    if (!conn) return;

    conn.disabled = disabled;

    // Handle audio muting for all instances
    for (const instanceId of this.getInstancesOfGraph(definitionId)) {
      this.setConnectionMuted(instanceId, edgeId, disabled);
    }
    this.pushDefinition(definitionId);
  }

  // ─────────────────────────────────────────────────────────────
  // Utilities
  // ─────────────────────────────────────────────────────────────

  /**
   * Resolve an exposed endpoint name to internal path
   */
  resolveEndpoint(definitionId: string, endpoint: string): string {
    const graphDefinition = this.graphDefinitions[definitionId];
    if (!graphDefinition) return endpoint;
    return resolveExposedPath(graphDefinition.api, endpoint);
  }

  /**
   * Get exposed endpoints for a graph definition
   * Returns map of exposed name -> ExposedEndpoint config
   */
  getExposedEndpoints(definitionId: string): Record<string, ExposedEndpoint> {
    const graphDefinition = this.graphDefinitions[definitionId];
    return graphDefinition?.api ?? {};
  }

  /**
   * Get all instances of a specific graph definition
   */
  getInstancesOfGraph(definitionId: string): string[] {
    return Array.from(this.instancesByDefinition[definitionId] ?? []);
  }

  // ─────────────────────────────────────────────────────────────
  // State Management
  // ─────────────────────────────────────────────────────────────

  private stateInitialized = false;

  /**
   * Apply session state to the runtime.
   * - Updates graph definitions
   * - Adds/removes nodes to match state
   * - Applies values (with diffing to avoid unnecessary updates)
   * - Wires/unwires connections
   */
  setState(state: { definitions: Record<string, GraphDefinition> }): void {
    const { definitions } = state
    // Update definitions
    for (const [id, def] of Object.entries(definitions)) {
      const graphDef: GraphDefinition = {
        id: def.id,
        nodes: def.nodes,
        connections: def.connections,
        api: def.api,
        config: def.config
      };
      this.graphDefinitions[id] = graphDef;
      if (!this.instancesByDefinition[id]) {
        this.instancesByDefinition[id] = new Set();
      }
    }


    // First time: dispose everything and rebuild
    if (!this.stateInitialized) {
      for (const instanceId of Object.keys(this.instances)) {
        this.disposeInstance(instanceId);
      }
      this.stateInitialized = true;
    } else {

      // Remove session definitions that no longer exist 
      for (const id of Object.keys(this.graphDefinitions)) {
        if (!definitions[id]) {
          delete this.graphDefinitions[id];
          delete this.instancesByDefinition[id];
        }
      }

      // Apply disabled connections from definitions
      // Connection.disabled is already part of definition data, just need to update runtime
      for (const [defId, def] of Object.entries(definitions)) {
        for (const conn of def.connections) {
          const edgeId = `${conn.source}->${conn.target}`;
          if (conn.disabled) {
            this.setConnectionMuted(defId, edgeId, true);
          } else {
            this.setConnectionMuted(defId, edgeId, false);
          }
        }
      }
    }

    // Apply nodes, values, and connections
    this.applyRootState();
  }

  /**
   * Load a project fresh - dispose all instances and re-instantiate from definitions.
   * Unlike setState which does incremental updates, this fully rebuilds the runtime.
   */
  loadProject(definitions: Record<string, GraphDefinition>): void {
    // Suppress emissions during load
    this.loading = true;

    try {
      // Register all definitions
      for (const [id, def] of Object.entries(definitions)) {
        this.graphDefinitions[id] = {
          id: def.id,
          info: def.info,
          viz: def.viz,
          vizNode: def.vizNode,
          nodes: def.nodes,
          connections: def.connections,
          api: def.api,
          config: def.config
        };
        if (!this.instancesByDefinition[id]) {
          this.instancesByDefinition[id] = new Set();
        }
      }

      // Remove definitions that no longer exist
      for (const id of Object.keys(this.graphDefinitions)) {
        if (!definitions[id] && id !== 'root') {
          delete this.graphDefinitions[id];
          delete this.instancesByDefinition[id];
        }
      }

      // Dispose all existing instances
      for (const instanceId of Object.keys(this.instances)) {
        try {
          this.disposeInstance(instanceId);
        } catch (e) {
          // Ignore disposal errors
        }
      }

      // Clear tracking
      this.instances = {};
      this.containedBy = {};
      this.wiredConnections = {};

      // Clear registries (renderers/audio from old project)
      clearVideoRegistry();
      clearAudioRegistry();

      // Fresh instantiate root
      this.instantiate('root', 'root');
    } finally {
      // Re-enable emissions
      this.loading = false;
    }

    // Emit definitions in dependency order (topological sort)
    // Child definitions must be pushed before parents that contain them
    const pushOrder = this.getDefinitionEmitOrder(definitions);
    for (const definitionId of pushOrder) {
      this.pushDefinition(definitionId);
    }
  }

  /**
   * Get definitions in dependency order (topological sort).
   * Leaf definitions first, definitions that depend on them later.
   */
  private getDefinitionEmitOrder(definitions: Record<string, GraphDefinition>): string[] {
    const defIds = Object.keys(definitions);
    const visited = new Set<string>();
    const result: string[] = [];

    const visit = (defId: string) => {
      if (visited.has(defId)) return;
      visited.add(defId);

      const def = definitions[defId];
      if (!def) return;

      // Visit dependencies first (node types that are graph definitions)
      for (const nodeEntry of Object.values(def.nodes)) {
        const nodeType = getNodeType(nodeEntry);
        if (definitions[nodeType] && nodeType !== defId) {
          visit(nodeType);
        }
      }

      result.push(defId);
    };

    for (const defId of defIds) {
      visit(defId);
    }

    return result;
  }

  /**
   * Apply state to the root instance.
   * Adds/removes nodes, applies values, wires connections.
   */
  private applyRootState(): void {
    const rootDef = this.graphDefinitions['root'];
    if (!rootDef) {
      throw new Error('Root definition not found');
    }

    const expectedNodeIds = new Set(Object.keys(rootDef.nodes));

    // Get or create root instance
    let instance = this.instances['root'];
    if (!instance) {
      instance = this.instantiate('root', 'root');
      return; // instantiate handles everything for new instances
    }

    const existingNodeIds = new Set(Object.keys(instance.nodes));

    // Remove nodes that no longer exist
    for (const nodeId of existingNodeIds) {
      if (!expectedNodeIds.has(nodeId)) {
        const node = instance.nodes[nodeId];
        this.graphIndex.removeNode('root', nodeId);
        node?.dispose();
        delete instance.nodes[nodeId];
        // Sub-instances use composite IDs: root/${nodeId}
        const subId = `root/${nodeId}`;
        if (this.instances[subId]) {
          this.graphIndex.removeInstance(subId);
        }
        delete this.instances[subId];
      }
    }

    // Add new nodes (two-pass: create all first, then initialize)
    // This ensures all nodes exist before any initialize() calls emit outputs
    const newNodes: Array<{ nodeId: string; node: INode }> = [];

    for (const nodeId of expectedNodeIds) {
      if (!existingNodeIds.has(nodeId)) {
        const nodeType = getNodeType(rootDef.nodes[nodeId]);
        const instanceConfig = rootDef.config[nodeId] ?? {};

        // Build initial values: base defaults + config overrides from containing def
        const initialValues: Record<string, any> = {};

        // Get base defaults (from plugin endpoints or sub-graph API)
        const endpoints = this.pluginRegistry.getEndpoints(nodeType);
        if (endpoints) {
          for (const [epId, ep] of Object.entries(endpoints)) {
            if (ep.value !== undefined) {
              initialValues[epId] = ep.value;
            }
          }
        } else {
          const subDef = this.graphDefinitions[nodeType];
          if (subDef) {
            for (const [exposedName, exposed] of Object.entries(subDef.api)) {
              if ((exposed as ExposedEndpoint).value !== undefined) {
                initialValues[exposedName] = (exposed as ExposedEndpoint).value;
              }
            }
          }
        }

        // Apply config overrides from containing definition
        for (const [key, val] of Object.entries(instanceConfig)) {
          if (!key.includes(':')) { // Skip metadata keys like "frequency:input"
            initialValues[key] = val;
          }
        }

        // Ensure node entry exists in store (even if no values)
        this.store.ensureNodeEntry('root', nodeId);

        // Write initial values to store
        for (const [epId, value] of Object.entries(initialValues)) {
          this.pushValue('root', nodeId, epId, value);
        }

        // Create node
        let node: INode;
        if (this.pluginRegistry.hasNode(nodeType)) {
          const context = this.createDeps('root', nodeId, nodeType);
          node = this.pluginRegistry.createNode(nodeType, nodeId, context);
          this.graphIndex.addNode('root', nodeId, node);
        } else {
          // Use composite ID for sub-instances to avoid collision when same definition is used multiple times
          const subInstanceId = `root/${nodeId}`;
          this.containedBy[subInstanceId] = 'root';
          // Pass config so sub-graph receives parent's config overrides
          node = this.instantiate(nodeType, subInstanceId, instanceConfig);
        }
        instance.nodes[nodeId] = node;
        newNodes.push({ nodeId, node });
      }
    }

    // Initialize new nodes after all nodes are in instance.nodes
    for (const { nodeId, node } of newNodes) {
      try {
        node.initialize();
        // Apply initial values from store
        const nodeValues = this.store.getNodeValues('root', nodeId);
        for (const [endpointId, value] of Object.entries(nodeValues)) {
          node.onParamChanged(value, endpointId);
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logger.error('Runtime', `${error.message} ${nodeId}.initialize`);
        this.logError(error, { nodeId, endpointId: 'initialize' });
      }
    }

    // Build set of endpoints with incoming connections
    const connectedTargets = new Set<string>();
    for (const conn of rootDef.connections) {
      connectedTargets.add(conn.target);
    }

    // Apply values from config (diff to avoid unnecessary updates)
    for (const [nodeId, endpoints] of Object.entries(rootDef.config)) {
      const node = instance.nodes[nodeId];
      if (node) {
        for (const [epId, value] of Object.entries(endpoints)) {
          // Skip metadata overrides (e.g., "frequency:input", "frequency:output")
          if (epId.includes(':')) continue;
          const endpointDef = this.getEndpoint('root', nodeId, epId);
          // Skip transient endpoints - they shouldn't be restored from server state
          if (endpointDef?.transient) continue;
          // Skip endpoints with incoming connections - connection is source of truth
          if (connectedTargets.has(`${nodeId}.${epId}`)) continue;
          const currentValue = this.store.getValue({ instancePath: 'root', nodeId, endpointId: epId });
          if (JSON.stringify(currentValue) !== JSON.stringify(value)) {
            // Store wraps value and returns payload
            const payload = this.store.setValue({ instancePath: 'root', nodeId, endpointId: epId }, value);
            node.onParamChanged(payload, epId);
          }
        }
      }
    }

    // Wire/unwire connections
    this.applyConnections(instance, rootDef);
  }




  /**
   * Apply connections for an instance based on the definition.
   * Wires new connections and unwires removed connections.
   */
  private applyConnections(instance: IGraphInstance, def: GraphDefinition): void {
    const expectedConnections = new Set<string>();
    for (const conn of def.connections) {
      expectedConnections.add(`${conn.source}->${conn.target}`);
    }

    // Get currently wired root-level connections
    const wired = this.wiredConnections[instance.instancePath] ?? new Set<string>();
    const rootWired = new Set<string>();
    for (const connKey of wired) {
      const [source, target] = connKey.split('->');
      const sourceParts = source.split('.');
      const targetParts = target.split('.');
      if (sourceParts.length === 2 && targetParts.length === 2) {
        rootWired.add(connKey);
      }
    }

    // Wire new connections
    for (const connKey of expectedConnections) {
      if (!wired.has(connKey)) {
        const [source, target] = connKey.split('->');
        this.activateConnection(instance, def, source, target);
      }
    }

    // Unwire removed connections
    for (const connKey of rootWired) {
      if (!expectedConnections.has(connKey)) {
        const [source, target] = connKey.split('->');
        this.deactivateConnection(instance, def, source, target);
      }
    }
  }




  /**
   * Get the transport
   */
  getTransport(): ITransport | null {
    return this.env.transport;
  }

  /**
   * Dispose all instances and clean up
   */
  dispose(): void {
    for (const instanceId of Object.keys(this.instances)) {
      // Child instances may already be disposed by their parent node's dispose()
      if (this.instances[instanceId]) {
        this.disposeInstance(instanceId);
      }
    }
    this.graphDefinitions = {};
  }
}
